import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { Portal } from 'react-portal';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getField } from 'app/components/SearchFilter/Constants';
import { generateMinMaxDateValue, generateMinMaxValue } from 'utils/filter_menu/minandmax';

import {
  streamSearchAgentChat,
  resolveLocationSuggestions,
  fetchSearchAgentChatHistory,
  AgentErrorCause,
} from 'app/AgentClients/searchAgentClient';
import {
  updateFilter,
  updateLocalFilter,
  clearFilter,
  clearLocalFilter,
  setSuggestionValue,
  setSuggestionLabel,
  setFavoriteItem,
  setAiSearchLabel,
  markAiSearchLabelEarlier,
  clearAiSearchLabel,
  selectLoading,
  selectStale,
  selectListingResultVersion,
  selectListingError,
  selectActiveContext,
  selectFilter,
  selectLocalFilter,
  selectLocation,
  isKnownFilterField,
} from 'data/search';

import { toAppliedFilterContext } from './appliedFilterContext';
import { ListingTypeOptions } from './searchFilterNew/shared/ConstantsNew';
import {
  filterFieldLabel,
  filterValueLabel,
  filterValueLabels,
  isListFilter,
  filterCategoryKey,
} from './searchFilterNew/shared/filterLabels';

import AIChatErrorCard from 'components/AIChatErrorCard';
import css from './style.scss';
import aiFilterCardHeaderIcon from 'assets/images/aiFilterCardHeaderIcon.svg';
import aiFilterCardLocationPin from 'assets/images/aiFilterCardLocationPin.svg';
import aiFilterCardCheck from 'assets/images/aiFilterCardCheck.svg';
import UnavailableIllustration from 'assets/images/ai-search-unavailable.svg';

const MAX_HISTORY_MESSAGES = 20;
export const MAX_PROMPT_WORDS = 160;
const STREAM_RENDER_INTERVAL_MS = 25;
const STREAM_CHARS_PER_TICK = 2;

// The two frontend-owned tools (Story 4): resolve_location is a genuine
// agent iteration and gets a real tool_result. ask_clarifying_question does
// not — its tool_use is deliberately never written into the history sent
// back to the model; the completed turn is represented as the plain
// rendered text instead (lead-in + question + numbered options), so the
// user's natural-language reply is an ordinary plain-text turn rather than
// a tool_result closing an open tool_use. Anthropic only validates the
// internal consistency of the array actually sent — a tool_use the FE
// chose not to record doesn't exist for it — so this sidesteps the wire
// requirement entirely instead of satisfying it, which also keeps
// MAX_ITERATIONS, prompt-injection scanning, and usage logging (all keyed
// off "is the last user message plain text") working correctly on a
// clarification reply, since it stays a normal turn boundary.
// Schemas stay generic per the design (no taxonomy/option enumeration here —
// the model fills in real values from the cached system-prompt taxonomy or
// resolve_location's own tool_result).
//
// apply_property_search_filters (Story 5) is a third frontend-owned tool,
// modeled on the search-ai-v2 POC's apply_search_filters (src/app/Search/
// SearchAIChat.js on that branch) — summary + optional location/clearExisting
// + a generic filters object — rather than the minimal {filters} shown in the
// S2 design doc's example, since the POC is the only place this has actually
// been built end-to-end. Applying is automatic the moment the tool_use
// arrives (Figma "AI Search - LONG CHAT", node 284:29787, only ever shows
// the already-applied state — no pending confirmation step), so the real
// result_count travels straight through this tool_result, same as the
// design describes: applying is synchronous here, so there's no
// user-interaction gap that could leave the tool_use dangling the way a
// click-gated Apply would (the same class of wire-protocol bug already
// found twice on this epic — see ask_clarifying_question above).
//
// Tool order is fixed (apply_property_search_filters, resolve_location,
// ask_clarifying_question) and must stay that way across every request —
// the backend caches the tools array via cache_control on the last entry,
// prefix-based, so reordering between requests misses the cache.
const MAX_RESOLVE_LOCATION_DEPTH = 3;
// Separate from MAX_RESOLVE_LOCATION_DEPTH — apply_property_search_filters
// recursing into itself (the model calling it again off its own tool_result)
// is a different failure mode than resolve_location retrying, and sharing
// one counter meant two applies could exhaust the location-retry budget and
// make a later, genuine resolve_location bail with a misleading "I couldn't
// resolve that location" (Fahim's PR #3344 review round 4, MAJOR).
const MAX_APPLY_DEPTH = 3;
const APPLY_FILTERS_SCHEMA_VERSION = 'v2';
// Welcome Back staleness window (PR #3361 review, MAJOR) — ATAI-154 returns
// snapshotAt precisely so a resume prompt doesn't outlive its relevance;
// past this window a stored search is treated as gone, same as no snapshot.
// The exact window is a product call worth confirming; 7 days is a starting
// guess, not a validated figure.
const MAX_SNAPSHOT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// Persistent per-browser session id (Story 7 §2/§4, ATAI-155) — replaces the
// old `search-${Date.now()}` ephemeral id (a fresh, unrelated session every
// mount) so chat_conversation_logs.session_id — and therefore history/resume
// — survives a reload. Code-checked 2026-08-26: no existing per-browser
// persistent-identifier pattern exists anywhere in propstream (every current
// localStorage use is UI-state) — this is new, not a reuse.
const SESSION_ID_STORAGE_KEY = 'aiSearchSessionId';
const CHAT_HAS_CONTENT_STORAGE_KEY = 'aiSearchChatHasContent';

function createSessionId() {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `search-${random}`;
}

function getOrCreatePersistentSessionId() {
  try {
    const existing = window.localStorage.getItem(SESSION_ID_STORAGE_KEY);
    if (existing) return existing;
    const created = createSessionId();
    window.localStorage.setItem(SESSION_ID_STORAGE_KEY, created);
    return created;
  } catch (err) {
    // Private browsing / storage disabled — degrade to the old ephemeral
    // behavior rather than crashing the panel.
    return createSessionId();
  }
}

export function hasPersistentSearchChatContent() {
  try {
    return window.localStorage.getItem(CHAT_HAS_CONTENT_STORAGE_KEY) === 'true';
  } catch (err) {
    return false;
  }
}

function setPersistentSearchChatContent(hasContent) {
  try {
    if (hasContent) {
      window.localStorage.setItem(CHAT_HAS_CONTENT_STORAGE_KEY, 'true');
    } else {
      window.localStorage.removeItem(CHAT_HAS_CONTENT_STORAGE_KEY);
    }
  } catch (err) {
    // Storage is only an expand affordance; the live panel state still owns content.
  }
}

// Rotates the persisted session id so the *next* mount starts a genuinely
// new conversation instead of resuming this one. Called from handleStartFresh
// below, and from Header/index.js's handleCloseChat/handleClear — those are
// deliberate "end this conversation" actions. NOT called from mode switch:
// that fires on every Manual<->AI toggle, including the very first switch
// into AI mode after a fresh page load, when there's no conversation to end
// (PR #3361 review + live testing — rotating there defeated the point of a
// persistent session).
export function resetPersistentSearchSession() {
  try {
    window.localStorage.setItem(SESSION_ID_STORAGE_KEY, createSessionId());
    window.localStorage.removeItem(CHAT_HAS_CONTENT_STORAGE_KEY);
  } catch (err) {
    // Private browsing / storage disabled — nothing persisted to rotate;
    // the next mount's getOrCreatePersistentSessionId() degrades the same way.
  }
}

// The geo-bearing subset of LocationFields (reducer.js) — presence of any of
// these means the location can actually scope a search. `stateId` is included:
// statewide search IS supported (ATAI-191), exactly as Manual Search supports
// it today — a state suggestion (`type: 'T'`) sets `stateId`
// (Header/index.js's handleChangeSuggestion) and setLocation builds the query
// off `geo = { stateId }` (data/search/reducer.js) with no granularity guard.
// Nationwide (no location at all) is still not a search: a location carrying
// none of these keys, or no location at all, fails this check.
// Split by what a usable value looks like: the three geo ids are database
// ids, the rest are text. `0` is not a place, and the model does echo one —
// a local run resolved "Los Angeles, CA" to `{label, cityId: 0}` (two keys,
// none of the `id`/`type`/`fips` a real suggestion row carries, so the model
// wrote it, not the endpoint), which the old `!= null && !== ''` test
// accepted. The apply branch then trusts an already-supported `location`
// without re-resolving it, so that zero went straight into the search and
// came back with 0 properties while the agent blamed the filters.
const SUPPORTED_LOCATION_ID_KEYS = ['cityId', 'countyId', 'stateId'];
const SUPPORTED_LOCATION_TEXT_KEYS = ['zip', 'fips', 'shapeDefinition'];

function hasSupportedLocation(location) {
  if (!location) {
    // no location at all — nationwide, which is not a search (ATAI-191)
    return false;
  }
  // a geo id, as a number or the string form a suggestion row can carry
  const hasGeoId = SUPPORTED_LOCATION_ID_KEYS.some((key) => Number(location[key]) > 0);
  if (hasGeoId) {
    return true;
  }
  return SUPPORTED_LOCATION_TEXT_KEYS.some((key) => {
    const value = location[key];
    if (typeof value === 'number') {
      // a zip that arrived as a number (90065) — same rule as the geo ids
      return value > 0;
    }
    // fips keeps its leading zero and shapeDefinition is a path, so both are
    // only ever strings — a blank one scopes nothing
    return typeof value === 'string' && value.trim() !== '';
  });
}

// The geo keys the model must never be believed about (ATAI-225). The
// resolve_location tool_result is stripped to {label, locationType} — the ids
// are deliberately never shown to it — so ANY of these arriving on an
// apply_property_search_filters `location` was written by the model, not by the
// endpoint, and is a guess.
//
// These three and not the other two because of what a guess can collide with.
// `cityId`/`countyId` are 6-digit surrogate keys the model has no way to
// produce (every city/county apply in the QA sample came back correct), but a
// state has a number the model does know — its federal FIPS code — and
// `view_states.id` is an alphabetical-rank key that occupies the same small
// integer range. Texas's FIPS is 48; `view_states.id` 48 is PENNSYLVANIA. The
// model wrote `stateId: 48`, this file believed it, and the search ran on
// Pennsylvania under a card reading "Texas, TX" — 15 of 24 statewide searches
// in one QA session. Every state is exposed, not just Texas: FL's FIPS 12 is
// Arizona's id, NY's 36 is Montana's. `zip` is the same shape of risk (a model
// produces "78701" for Austin effortlessly) and `fips` likewise, so all three
// are re-resolved rather than trusted.
const UNVERIFIABLE_LOCATION_KEYS = ['stateId', 'zip', 'fips'];

// True when the model's own `location` carries one of the keys above. Such a
// location is treated as unresolved so the apply branch re-resolves it through
// resolveLocationSuggestions and takes the id off the real suggestion row
// (remapSuggestionLocationFields), instead of short-circuiting on the guess.
function hasUnverifiableLocationId(location) {
  if (!location) {
    // no location at all — nothing to distrust
    return false;
  }
  return UNVERIFIABLE_LOCATION_KEYS.some((key) => {
    const value = location[key];
    if (typeof value === 'number') {
      // stateId, or a zip that arrived as a number — same rule as
      // hasSupportedLocation's own id test
      return value > 0;
    }
    // zip/fips as the strings they normally are; a blank one scopes nothing
    // and is not worth forcing a re-resolve over
    return typeof value === 'string' && value.trim() !== '';
  });
}

// The model's `location` with its guessed keys dropped and everything it can
// legitimately hold kept. Dropping rather than rejecting the whole object
// matters twice over: a `location` that pairs a real cityId with a guessed
// stateId still searches the city it named (rejecting outright would ask the
// user to re-phrase a location they gave correctly), and a `location` that is
// nothing BUT a guessed key reads as no naming at all, so the apply branch
// falls through to the conversation's remembered location instead of asking —
// which is what ATAI-200 exists to prevent.
function stripUnverifiableLocationKeys(location) {
  if (!hasUnverifiableLocationId(location)) {
    // nothing guessed — hand the object back untouched
    return location;
  }
  const trusted = { ...location };
  UNVERIFIABLE_LOCATION_KEYS.forEach((key) => delete trusted[key]);
  return trusted;
}

// The suggestion endpoint's address-type codes, verified against
// propstream-be's `AddressTypes` enum and `LocationService.suggest`'s own
// SQL: P property, S street, C city, N county, T state, Z zip, G geometry.
// A single opaque letter is not something the model can reason about, so
// every code the agent ever sees is paired with a plain-English name here
// and the tool_result carries the name, not the letter (ATAI-191 — the
// agent could not tell a state row apart from a city row).
const LOCATION_TYPE_NAMES = {
  P: 'property',
  S: 'street',
  C: 'city',
  N: 'county',
  T: 'state',
  Z: 'zip',
  G: 'area',
};

// Reverse of LOCATION_TYPE_NAMES, for turning the model's echoed
// `locationType` back into the endpoint's own code.
const LOCATION_TYPE_CODES = Object.keys(LOCATION_TYPE_NAMES).reduce(
  (m, code) => ({ ...m, [LOCATION_TYPE_NAMES[code]]: code }),
  {}
);

// The address types AI Search asks the endpoint for. City/county/state/zip
// are exactly the scopes a filter search can run on; P (single property)
// and S (street) can't scope one — they'd fail hasSupportedLocation anyway,
// and they rank ahead of the real answer on address-shaped queries. Passing
// T also matters on its own: propstream-be only matches queries shorter
// than 4 characters when T is requested (LocationService.MIN_SUGGEST_LENGTH
// / `shortStateQuery`), which is how a bare "TX" resolves at all.
const SEARCH_SCOPE_LOCATION_TYPES = 'CNTZ';

// Mirrors Header/index.js's handleChangeSuggestion type->id remap. A raw
// suggestion row from resolveLocationSuggestions only carries {id, type,
// label} for the types whose own id column the endpoint doesn't select —
// a state row is `{id, type: 'T', label}` with no stateId of its own, the
// numeric id itself IS the stateId (confirmed against LocationService's
// suggest SQL: it selects city_id, county_id, fips and zip, but no
// state_id, so C/N/Z rows arrive already carrying their field and only T
// and P have to be derived from `id`). A location already carrying
// cityId/countyId/etc. (the model's own direct input, or
// context.currentLocation) is left untouched — the per-type branches below
// only fire when that field isn't already set.
function remapSuggestionLocationFields(loc) {
  if (!loc) {
    return loc;
  }
  const { id } = loc;
  const type = locationTypeCode(loc);
  const remapped = { ...loc };
  if (type === 'P' && remapped.propertyId == null) {
    // single property row — the row id is the property id
    remapped.propertyId = id;
  } else if (type === 'C' && remapped.cityId == null) {
    // city row — the row id is the city id
    remapped.cityId = id;
  } else if (type === 'T' && remapped.stateId == null) {
    // state row — the row id is the state id (statewide search, ATAI-191)
    remapped.stateId = id;
  } else if (type === 'N' && remapped.countyId == null) {
    // county row — the row id is the county id
    remapped.countyId = id;
  }
  return remapped;
}

// A location can reach here carrying the endpoint's own single-letter type
// (a raw suggestion row) or the plain-English `locationType` the model was
// given and echoes back on apply_property_search_filters. Normalize both to
// the letter so one remap/matching path covers them.
function locationTypeCode(loc) {
  if (!loc) {
    return undefined;
  }
  if (loc.type && LOCATION_TYPE_NAMES[loc.type]) {
    // raw suggestion row — already the endpoint's own code
    return loc.type;
  }
  if (loc.locationType) {
    // the model's echoed plain-English type ('state', 'city', …)
    return LOCATION_TYPE_CODES[String(loc.locationType).toLowerCase()];
  }
  return undefined;
}

// Picks which suggestion row the model meant, out of a fresh re-resolve of
// the label it echoed back. Label alone is ambiguous — a state and a city
// can carry the same one ("Washington") — so the row's type is matched too
// whenever the model told us which type it picked. Falls back through
// label-only, then type-only (the label text can differ from the row's own
// once the model has retyped it), and only then to the top row.
function pickLocationMatch(matches, requested) {
  const rows = matches || [];
  const label = requested?.label;
  const wantedType = locationTypeCode(requested);

  if (wantedType) {
    // the model named a type — trust label + type over either alone
    const exact = rows.find((m) => m.label === label && m.type === wantedType);
    if (exact) {
      return exact;
    }
  }
  const byLabel = rows.find((m) => m.label === label);
  if (byLabel) {
    // same place by label, type unstated or unmatched
    return byLabel;
  }
  if (wantedType) {
    // label was retyped/reformatted — the named type is the better signal
    const byType = rows.find((m) => m.type === wantedType);
    if (byType) {
      return byType;
    }
  }
  // Nothing matched — the endpoint's own top-ranked row, but only among the
  // types a filter search can actually scope on. A property row remaps to
  // `propertyId` and a street row carries no id at all, so handing either
  // back fails hasSupportedLocation in the apply branch, where a location the
  // model *did* name reads as unresolvable and the agent asks the user for a
  // location they already gave — the ATAI-200 symptom by a second route.
  // Better to return nothing and let that branch ask for a re-phrase than to
  // return a row the search cannot use.
  return rows.find((m) => SEARCH_SCOPE_LOCATION_TYPES.includes(locationTypeCode(m))) || null;
}

// foreclosureStatus2Class's real value set (PreForeclosureBank.js) — a
// String field per reducer.js's FilterFields, not a boolean.
const FORECLOSURE_STATUS_CLASS_VALUES = new Set(['PreForeclosure', 'PreForeclosureExclude']);

// listingType is validated against its exact value list — imported from
// ConstantsNew.js's ListingTypeOptions (the real source of truth the manual
// filter panel itself uses) rather than a hand-duplicated copy, so a future
// add/remove/rename there can't silently desync this validator.
const LISTING_TYPE_CODES = new Set(ListingTypeOptions.map((o) => o.value));

// listingType's stored value is a short code ('P', 'V', 'SBTW1', ...), not a
// display string — resolve it through the same options list for the chip,
// same as every other distress-category chip (Vacant, Liens, ...) shows its
// category name, not an internal value.
const LISTING_TYPE_LABELS = new Map(ListingTypeOptions.map((o) => [o.value, o.label]));

// Year-type Min/Max fields get a plausible-range clamp instead of the
// generic "Min/Max fields are >= 0" rule below. Constants.js has no distinct
// 'year' field type (yearBuilt/delinquentTaxYear are just 'number'), so this
// stays a hand-picked set rather than metadata-driven.
const YEAR_FIELD_BASES = new Set(['yearBuilt', 'delinquentTaxYear']);
const MIN_PLAUSIBLE_YEAR = 1700;

// Real boolean fields with NO entry of their own in FilterFieldMap, so
// getField() can't tell validateFilterValue they're boolean: lienActive/
// poolAvailable/garageAvailable/basementAvailable are each modeled only as
// a *sibling* field's includeKey (lienTypeCode/poolType/garageType/
// basementType); bankruptcyActive/divorceActive/atticAvailable don't appear
// anywhere in Constants.js at all. lienActive/bankruptcyActive/divorceActive
// are 3 of the design's own 7 AND-combinable distress booleans — without
// this fallback the metadata-driven check below would silently drop all 7,
// a worse regression than the hand-picked list it replaced. Verified by
// grepping Constants.js for every field this replaced (not spot-checked) —
// these 7 were the only misses out of 20.
const BOOLEAN_FIELDS_MISSING_FROM_MAP = new Set([
  'lienActive',
  'bankruptcyActive',
  'divorceActive',
  'poolAvailable',
  'garageAvailable',
  'basementAvailable',
  'atticAvailable',
]);

// Value-validation (Story 5 §2): the existing constrainObject(FilterFields)
// in the reducer already strips unknown *keys* — this adds the missing
// *value* checks (type/range/enum) before a key that does pass the whitelist
// ever reaches updateFilter. Driven by the real field metadata off
// getField(key) — the same live descriptor already imported for chip
// labels — rather than hand-maintained suffix/set-based rules. The
// hand-picked boolean set missed ~9 real boolean fields the reducer's own
// whitelist accepts (Fahim's PR #3344 review round 4, BLOCKER + MAJOR +
// MINOR #1).
//
// F1 (ATAI-139/ATAI-136): a prior revision of this function dropped its
// percent-field [0,1] clamp on the theory that percent fields are stored as
// whole percents ('20' for 20%) — that was backwards. Constants.js's own
// formatPercent(value * 100) display path, and the manual UI's
// AutoSearchDropdown (searchFilterNew/shared/Autosearch/index.js
// optionSelected), which divides its whole-percent option values
// (PercentMinMaxOptions: '10'..'100') by 100 before ever calling
// onChangeValue/updateFilter, both confirm the *stored* value is a fraction
// of 1 — verified live via network capture (manual UI sends
// estimatedEquityPercentMin=0.4 for a 40% selection). The model reasons in
// whole percents same as those dropdown options ("at least 50%" -> 50), so
// that mismatch reached updateFilter unnormalized and every AI percent
// filter (Equity %, LTV %, Growth %, Gross Yield %, Improvement to Tax
// Value %, Interest Rate %) silently 100x'd, always returning 0 results.
function validateFilterValue(key, value, fromModel = false) {
  if (value == null) return undefined;

  if (key === 'listingType') {
    return typeof value === 'string' && LISTING_TYPE_CODES.has(value) ? value : undefined;
  }

  if (key === 'foreclosureStatus2Class') {
    return typeof value === 'string' && FORECLOSURE_STATUS_CLASS_VALUES.has(value)
      ? value
      : undefined;
  }

  const field = getField(key);
  const fieldType = field?.type;

  if (fieldType === 'boolean' || (!field && BOOLEAN_FIELDS_MISSING_FROM_MAP.has(key))) {
    return typeof value === 'boolean' ? value : undefined;
  }

  if (fieldType === 'date') {
    // Represented as a moment, matching the manual UI's own
    // getMinMaxDateToUpdate (src/utils/filter_menu/minandmax.js) — a raw
    // ISO string reaches /listing as a string where the backend has only
    // ever received a long (Search/index.js converts only moments to epoch
    // millis on the way out via moment.isMoment(filt[k])).
    return typeof value === 'string' && !Number.isNaN(Date.parse(value))
      ? moment(value)
      : undefined;
  }

  if (
    fieldType === 'number' ||
    fieldType === 'decimal' ||
    fieldType === 'currency' ||
    fieldType === 'percent'
  ) {
    const num = Number(value);
    if (!Number.isFinite(num)) return undefined;
    const base = /^(.*)(Min|Max)$/.exec(key)?.[1] || key;
    if (YEAR_FIELD_BASES.has(base)) {
      const maxYear = new Date().getFullYear() + 1;
      return Math.min(maxYear, Math.max(MIN_PLAUSIBLE_YEAR, num));
    }
    // Normalize a whole-percent value from the model (50 for "50%") into
    // the fraction-of-1 the UI/backend actually store (0.5) — see the F1
    // note above. Only when |value| > 1: this function also re-validates
    // buildResumeState's already-stored (already-fraction) snapshot filters
    // on resume, and a value already in [-1, 1] is left alone rather than
    // divided a second time — dividing unconditionally would silently wreck
    // every resumed percent filter (0.5 -> 0.005) the moment this bug was
    // fixed. The one edge case this trades away: "at least 1% equity" (the
    // model sending exactly 1) reads as an already-normalized 100% instead
    // of 1% — accepted given how implausible that literal query is versus
    // resume correctness.
    const isWholePercent = fromModel || Math.abs(num) > 1;
    const normalized = fieldType === 'percent' && isWholePercent ? num / 100 : num;
    return field?.negativeAllowed ? normalized : Math.max(0, normalized);
  }

  if (Array.isArray(value)) {
    const strings = value.filter((v) => typeof v === 'string');
    return strings.length ? strings : undefined;
  }

  // Text inputs (schoolDistrict, mlsListingKeyword) and list-type fields not
  // already caught by the array check above — basic string/number type
  // check only.
  return typeof value === 'string' || typeof value === 'number' ? value : undefined;
}

// A comparable numeric value for the min/max pair-sanity check below — a
// plain number, or a moment's epoch millis (date fields are now stored as
// moment objects, not raw strings; typeof min === 'number' alone would
// never have caught an inverted date pair).
function comparableValue(value) {
  if (typeof value === 'number') return value;
  if (moment.isMoment(value)) return value.valueOf();
  return null;
}

// Drops values that fail validateFilterValue, then clamps every Min/Max pair
// sharing a base name (dropping both if Min ends up > Max after the
// per-field clamps above) — the design's "min/max pair sanity" rule. Runs on
// date pairs too, not just numeric ones (Fahim's PR #3344 review, MINOR).
function validateFilters(rawFilters, fromModel = false) {
  const validated = {};
  Object.keys(rawFilters || {}).forEach((key) => {
    const value = validateFilterValue(key, rawFilters[key], fromModel);
    if (value !== undefined) validated[key] = value;
  });

  const bases = new Set();
  Object.keys(validated).forEach((key) => {
    const m = /^(.*)(Min|Max)$/.exec(key);
    if (m) bases.add(m[1]);
  });
  bases.forEach((base) => {
    const min = comparableValue(validated[`${base}Min`]);
    const max = comparableValue(validated[`${base}Max`]);
    if (min != null && max != null && min > max) {
      delete validated[`${base}Min`];
      delete validated[`${base}Max`];
    }
  });

  return validated;
}

// Newest-first history off the wire (ATAI-154) reversed to chronological can
// still be unsafe to feed straight into the model-facing `history` array, for
// two reasons the backend doesn't guard against (PR #3361 review):
//  - chat_conversation_logs writes one row per HTTP request, not per
//    conversational turn, so a tool round-trip (resolve_location,
//    apply_property_search_filters) re-persists the same user_message
//    across 2-3 rows in a row (MAJOR: duplicated transcript on reload).
//  - a model turn that emitted only a tool_use with no text delta persists
//    an assistant row with a blank aiResponse; when that row falls inside
//    the returned window, two 'user' rows end up adjacent once the blank
//    assistant row is skipped (BLOCKER: breaks Anthropic's role alternation
//    and 400s every subsequent message).
// This single pass fixes both: drop text-free assistant rows, collapse a
// consecutive duplicate user row, and — as a safety net for any other
// same-role adjacency — merge into the previous turn rather than ever emit
// two consecutive same-role messages.
function buildPriorTurns(rawMessages) {
  const chronological = (rawMessages || []).slice().reverse();
  const turns = [];
  let lastUserContent = null;
  chronological.forEach((m, i) => {
    const content = typeof m.content === 'string' ? m.content : JSON.stringify(m.content ?? '');
    if (m.role === 'assistant' && !content.trim()) return;

    if (m.role === 'user') {
      if (content === lastUserContent) return;
      lastUserContent = content;
    }

    const last = turns[turns.length - 1];
    if (last && last.role === m.role) {
      last.content = last.content ? `${last.content}\n\n${content}` : content;
      return;
    }

    turns.push({
      id: `history-${i}-${m.createdAt || i}`,
      role: m.role,
      content,
    });
  });
  return turns;
}

// Re-validates a stored search-state snapshot (Story 7 §2/§5, ATAI-155)
// before it's ever offered as a "Welcome back" resume prompt — a snapshot
// is not trusted just because it was persisted. Returns null (fall back to
// starting fresh, no card shown) unless ALL of:
//  - the schema/tool version is still current (APPLY_FILTERS_SCHEMA_VERSION)
//  - the snapshot isn't older than MAX_SNAPSHOT_AGE_MS (PR #3361 review,
//    MAJOR: snapshotAt was ignored, so a months-old search kept prompting
//    "Welcome back" forever)
//  - the stored location re-resolves to a real, search-scopable match
//    (city/county/state/zip/fips/shape — see hasSupportedLocation)
//
// A location-only search (no distress/category filters at all — "All
// properties in Los Angeles, CA") is a legitimate thing to resume; this
// used to require Story 5's validated filters to be non-empty, which
// silently killed Welcome Back for exactly that case (confirmed live:
// snapshot had `filters: {}`, resume always returned null).
//
// The snapshot only ever stores a single collapsed `resolvedLocationId`
// (cityId ?? countyId ?? stateId ?? zip ?? fips) — not which field it came
// from — because that's the field the backend's own persisted schema
// actually has room for (confirmed live: a `resolvedLocation` object sent
// in its place round-trips as `resolvedLocationId`/`resolvedLocationLabel:
// null` — the backend has a fixed shape for this column, it is not opaque
// passthrough JSON as this comment previously assumed). So the location
// still has to be re-resolved from resolvedLocationLabel via
// resolveLocationSuggestions. What changed from the ORIGINAL PR code:
// no more blind `matches[0]` fallback on a label miss — a candidate must
// match BOTH the exact label AND the stored resolvedLocationId (collapsed
// the same way the write side collapses it), or resume is refused outright
// (PR #3361 review, MAJOR: a miss used to silently substitute a different
// place than the one shown on the card).
async function buildResumeState(snapshot) {
  if (!snapshot) return null;
  if (snapshot.toolSchemaVersion !== APPLY_FILTERS_SCHEMA_VERSION) return null;
  if (snapshot.snapshotAt && Date.now() - Date.parse(snapshot.snapshotAt) > MAX_SNAPSHOT_AGE_MS) {
    return null;
  }
  if (!snapshot.resolvedLocationLabel) return null;
  // ATAI-278 (PR #3502 review, Mani): a snapshot written before this fix can
  // carry a filter key PropStream doesn't have — the reducer would drop it
  // silently on Continue, so the resumed search would quietly be broader than
  // the one being offered. That is this ticket's bug reached through the
  // resume door.
  //
  // Not offering the resume at all is the answer, rather than refusing at
  // Continue (a dead end on a button the user pressed to restore their own
  // state) or dropping the key silently (the exact pattern this ticket
  // exists to remove). It is also what this function already does five times
  // above for a snapshot it cannot faithfully restore — wrong schema version,
  // too old, no location, unresolvable location. The user is never shown an
  // offer that cannot be honoured, so there is nothing to mislead them about.
  //
  // Self-limiting: only snapshots written before this ships can contain such a
  // key, and they age out via MAX_SNAPSHOT_AGE_MS within a week.
  if (Object.keys(snapshot.filters || {}).some((key) => !isKnownFilterField(key))) return null;

  const validatedFilters = validateFilters(snapshot.filters);

  let resolvedLocation = null;
  try {
    const matches = await resolveLocationSuggestions(
      snapshot.resolvedLocationLabel,
      SEARCH_SCOPE_LOCATION_TYPES
    );
    resolvedLocation =
      matches.map(remapSuggestionLocationFields).find((m) => {
        if (m.label !== snapshot.resolvedLocationLabel) return false;
        if (snapshot.resolvedLocationId == null) return true;
        const collapsedId = m.cityId ?? m.countyId ?? m.stateId ?? m.zip ?? m.fips ?? null;
        return String(collapsedId) === String(snapshot.resolvedLocationId);
      }) || null;
  } catch (err) {
    resolvedLocation = null;
  }
  if (!hasSupportedLocation(resolvedLocation)) return null;

  return {
    summary: snapshot.summary || 'your last search',
    // ATAI-286: the raw summary for the results-header label, which has its
    // own "no description" wording.
    labelSummary: snapshot.summary || '',
    locationLabel: snapshot.resolvedLocationLabel,
    resolvedFilters: validatedFilters,
    resolvedLocation,
  };
}

const TOOLS = [
  {
    name: 'apply_property_search_filters',
    description:
      'Apply the current search intent as real PropStream filters, once location (resolve_location) and any distress-category ambiguity (ask_clarifying_question) are already resolved. Applies immediately — one-way, no undo — and returns result_count plus result_count_status in the tool_result. Trust result_count ONLY when result_count_status is "confirmed"; a confirmed 0 genuinely means no properties matched. For any other status ("timeout", "error", "not_started") result_count is null and the count is simply unknown — the filters were still applied and the results may still be loading, so confirm that the search was applied and say the count could not be confirmed. Never describe an unconfirmed result as no properties found, and never state a number for it. `summary` is one short customer-friendly sentence describing the search (e.g. "Vacant properties in Phoenix, AZ"). `filters` is a flat key/value object grounded in the system prompt\'s filter taxonomy — never invent a field name or value outside it. `location` identifies a new location to search by the `label` and `locationType` of the resolve_location match you picked, passed back exactly as that tool returned them — that pair is what says which place this is, and PropStream looks the place up itself from it. Omit `location` entirely to keep whatever location is already active. Never put a state, zip or county code in it: you are not given place ids, so any you write would be a guess, and a guessed one resolves to a real but different place. `clearExisting` (default true) replaces the current filters instead of merging with them.',
    input_schema: {
      type: 'object',
      properties: {
        summary: { type: 'string' },
        clearExisting: { type: 'boolean' },
        location: {
          type: 'object',
          properties: {
            // stateId/zip/fips are deliberately absent (ATAI-225). The model
            // is never shown an id by resolve_location, so it can only ever
            // guess these three, and a guessed one resolves to a real but
            // wrong place — `stateId: 48` (Texas's FIPS) is Pennsylvania's
            // `view_states.id`. `additionalProperties: false` below keeps them
            // out of the payload; the component re-resolves from label +
            // locationType instead. A zip or county search still works — its
            // suggestion row carries those fields, and the row is trusted.
            cityId: { type: 'number' },
            countyId: { type: 'number' },
            shapeDefinition: { type: 'string' },
            label: { type: 'string' },
            locationType: {
              type: 'string',
              enum: ['state', 'city', 'county', 'zip', 'area'],
              description:
                "The picked match's own type, copied from resolve_location's result. A whole state is a valid scope — pass locationType 'state' rather than narrowing the search to a city inside it.",
            },
          },
          additionalProperties: false,
        },
        filters: {
          type: 'object',
          description:
            'Filter key/value pairs from the taxonomy in the system prompt (field names/types/enum values grounded there, not in this schema).',
          additionalProperties: true,
        },
      },
      required: ['summary', 'filters'],
      additionalProperties: false,
    },
  },
  {
    name: 'resolve_location',
    description:
      "Resolve a free-text place name to PropStream's location suggestions so a search can be scoped to it. Returns matches as {label, locationType} where locationType is 'state', 'city', 'county', 'zip' or 'area' — use it to tell the candidates apart (a state and a city can share a name, e.g. \"Washington\"). A whole state is a valid search scope: when the user names a state, pick the locationType 'state' match instead of asking them to narrow it down or substituting a city. Pass the picked match's label and locationType back on apply_property_search_filters.",
    input_schema: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'The place name to resolve, exactly as the user said it.',
        },
      },
      required: ['query'],
      additionalProperties: false,
    },
  },
  {
    name: 'ask_clarifying_question',
    description:
      "Ask the user to pick one or more options when a request is ambiguous. Does not get a tool_result — the user's pick arrives as the next plain conversation turn.",
    input_schema: {
      type: 'object',
      properties: {
        question: { type: 'string' },
        options: { type: 'array', items: { type: 'string' } },
      },
      required: ['question', 'options'],
      additionalProperties: false,
    },
  },
];

// Client-side rejection — never leaves the browser, so it has no backend cause.
const TOO_LONG = 'too_long';

// The agent turn itself succeeded and the filters were applied — it's the
// /listing search behind them that never came back (ATAI-192). Not an
// AgentErrorCause for that reason: nothing went wrong with the agent request.
const SEARCH_TIMED_OUT = 'search_timed_out';

// Outcome of waiting for the applied search's results. Only CONFIRMED carries
// a real count; everything else means "we do not know", and must never be
// flattened to 0 — that flattening is the ATAI-192 bug.
const SearchResultStatus = {
  CONFIRMED: 'confirmed',
  TIMEOUT: 'timeout',
  ERROR: 'error',
  NOT_STARTED: 'not_started',
};

// Matches the backend's own 300s ceiling on the listing request. A county-wide
// search with several filters routinely runs past the old 15s budget, and the
// old budget expiring was reported to the model as a result count of 0.
const SEARCH_RESULT_TIMEOUT_MS = 300000;
// How long to keep waiting for the stale-flag watcher to even start a search
// before concluding nobody is going to.
const SEARCH_START_GRACE_MS = 2000;
// When a search passes this, tell the user it's still running rather than
// leaving the panel on bare loading dots for up to five minutes.
const SLOW_SEARCH_NOTICE_MS = 60000;

// ATAI-278: what the panel says when the model asks for a filter PropStream
// doesn't have. Product still owns the final wording — it's marked
// "For: Product" on the ATAI-246 design — so every string lives here and
// nowhere else, making their edit a one-line change.
//
// Grouped into the filter *families* production actually shows the model
// inventing (30 days to 21 Sep 2026: ownerName x4, radiusMiles x4, address x3,
// address+zip x2, radius x2, apn/parcelNumber/streetAddress x1 each), so the
// refusal can name a real alternative instead of shrugging. Every alternative
// offered below is a genuine filter, checked against the taxonomy — promising
// one that doesn't exist would just restage this bug one level down.
//
// Anything outside these families falls back to the generic line rather than a
// humanised field name: humanising reads fine for `ownerName` ("owner name")
// and badly for `radiusMiles` ("radius miles"), and an unguarded humanise is
// precisely what manufactured the bogus "Owner Name" chip this ticket kills.
// A raw internal field name must never reach a customer.
const UNSUPPORTED_FILTER_FAMILIES = [
  {
    keys: ['ownerName', 'ownerFirstName', 'ownerLastName', 'ownerCompanyName'],
    message:
      "I can't search by owner name, so I haven't run this search. I can narrow by owner type " +
      "(individual or company), whether the owner lives at the property, how long they've owned it, " +
      'or how many properties they own. Want me to try one of those instead?',
  },
  {
    // `zip` deliberately NOT listed here (PR #3502 review, Mani): production
    // only ever saw it paired with address/streetAddress, so the pair still
    // matches this family on the address key — but a bare `{zip}` would have
    // matched too, and then refused the request while suggesting a zip as the
    // alternative, which contradicts itself. Unpaired, it now falls to the
    // generic message instead.
    keys: ['address', 'streetAddress', 'apn', 'parcelNumber'],
    message:
      "I can't search for one specific address or parcel number — AI Search covers an area rather " +
      "than a single property, so I haven't run this search. The location search bar at the top of " +
      "the page will find a single address. Or give me a city, county or zip and I'll search there.",
  },
  {
    keys: ['radius', 'radiusMiles', 'distance', 'latitude', 'longitude'],
    message:
      "I can't search within a distance of a point, so I haven't run this search. I can search a " +
      'city, county, zip or state — want me to use one of those?',
  },
];

const UNSUPPORTED_FILTER_FALLBACK =
  "That isn't something I can filter on, so I haven't run this search. Tell me what matters most " +
  "about the properties you're after and I'll work with the filters I do have.";

// First matching family wins. A request naming two unsupported things is rare
// enough not to warrant a stitched-together sentence, and the tool_result still
// carries every offending key, so the model's own follow-up can cover the rest.
function unsupportedFilterMessage(keys) {
  const family = UNSUPPORTED_FILTER_FAMILIES.find((candidate) =>
    keys.some((key) => candidate.keys.includes(key))
  );
  return family ? family.message : UNSUPPORTED_FILTER_FALLBACK;
}

// S8's failure states, keyed by the cause the agent client reports.
// Copy is transcribed from the S8 error-state frames in the AI Search Figma
// file (page "🟡 AI Search", nodes 310:20527 / 434:32073 / 637:24885 /
// 637:25618) — do not reword.
//
// The design draws one card for every non-rate-limit failure, so INCOMPLETE,
// PROCESSING_ERROR, SERVICE_UNAVAILABLE and UNKNOWN all share the generic
// card rather than each carrying a bespoke string.
//
// TRUNCATED and MAX_ITERATIONS reuse the card's shape but override its body:
// Figma has no frame for either, but that's silence, not a copy decision —
// ATAI-150 specifies the truncation wording, and ATAI-142's coverage review
// (item 3) settled on the backend's own message for MAX_ITERATIONS.
const GENERIC_FAILURE = {
  title: "We couldn't complete your request",
  body: 'Something went wrong while AI Search was processing your request. Please try again or rephrase your search.',
  retry: true,
};

const ERROR_CARDS = {
  [AgentErrorCause.SERVICE_UNAVAILABLE]: GENERIC_FAILURE,
  [AgentErrorCause.PROCESSING_ERROR]: GENERIC_FAILURE,
  [AgentErrorCause.INCOMPLETE]: GENERIC_FAILURE,
  [AgentErrorCause.UNKNOWN]: GENERIC_FAILURE,
  // Cause-specific body quoted verbatim from ATAI-150 — same card as the
  // processing-error state, different wording, because a truncated answer is
  // worth retrying only after the request is narrowed.
  [AgentErrorCause.TRUNCATED]: {
    ...GENERIC_FAILURE,
    body:
      'Your request may be too complex for AI Search to complete in one response. ' +
      'Try narrowing it — fewer filters or criteria at once — then try again.',
  },
  // No PRD copy exists for this one, and retrying can't clear it — show the
  // backend's own message rather than invent a product string, and drop the
  // CTA. Raised on ATAI-142 for a copy/CTA decision.
  [AgentErrorCause.MAX_ITERATIONS]: {
    title: null,
    body: 'This conversation has too many pending steps. Please start a new message.',
    retry: false,
  },
  [AgentErrorCause.RATE_LIMITED_DAILY]: {
    title: 'You’ve reached today’s AI Search limit',
    body: 'You’ve used all of your AI Search requests for today. You can continue searching tomorrow.',
    retry: false,
  },
  [AgentErrorCause.RATE_LIMITED_MINUTE]: {
    title: 'You’re searching a little too quickly',
    body: 'Please wait a moment before sending another request.',
    retry: false,
  },
  // No PRD/Figma copy exists for this state — the design predates it. Wording
  // is deliberate about the split outcome: the filters really were applied (the
  // grid will fill in whenever the listing API answers), only the count is
  // unknown. Retry re-sends the same prompt, which is the useful action here.
  [SEARCH_TIMED_OUT]: {
    title: 'Your search is taking longer than expected',
    body:
      "The filters were applied, but the property results didn't come back in time. " +
      'Try running the search again, or narrow it down.',
    retry: true,
  },
  [TOO_LONG]: {
    title: 'Your search is too long',
    body: `Please keep your request under ${MAX_PROMPT_WORDS} words. The AI can ask follow-up questions to gather any additional details.`,
    retry: false,
  },
};

function errorCardFor(cause) {
  return ERROR_CARDS[cause] || ERROR_CARDS[AgentErrorCause.UNKNOWN];
}

export function countWords(text) {
  return (text || '').trim().split(/\s+/).filter(Boolean).length;
}

function isToolResultMessage(msg) {
  return (
    msg?.role === 'user' &&
    Array.isArray(msg.content) &&
    msg.content.some((block) => block?.type === 'tool_result')
  );
}

// A fixed-length slice can land exactly between a tool_use and its
// tool_result (resolve_location and apply_property_search_filters both push
// that pair) — the Anthropic API rejects a tool_result with no preceding
// tool_use in the same array. If the cut leaves an orphaned tool_result at
// the front, drop it too; its tool_use is already gone either way, so there
// is nothing worth keeping it for.
function trimHistory(history) {
  if (history.length <= MAX_HISTORY_MESSAGES) return history;
  let start = history.length - MAX_HISTORY_MESSAGES;
  while (start < history.length && isToolResultMessage(history[start])) {
    start += 1;
  }
  return history.slice(start);
}

// Every bail-out path below must still hand back a history that ends on an
// assistant turn — the Messages API requires strict user/assistant
// alternation, and a history left ending on a user turn means the next
// handleSend appends a second consecutive user turn and gets rejected.
function withAssistantReply(baseHistory, text) {
  return trimHistory([...baseHistory, { role: 'assistant', content: text }]);
}

// Lightweight inline markdown — bold/italic/code + paragraph breaks only.
// Search Assistant replies are conversational prose; the richer table/heading
// rendering in PropertyAIChat.js is property-analysis specific and out of
// scope here.
function renderInline(text) {
  const parts = [];
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2]) parts.push(<strong key={m.index}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={m.index}>{m[3]}</em>);
    else if (m[4])
      parts.push(
        <code key={m.index} className={css.chatInlineCode}>
          {m[4]}
        </code>
      );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function MessageText({ text }) {
  return text.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {renderInline(line)}
    </React.Fragment>
  ));
}

// Mirrors PropertyAIChat.js's toggle-button "AI star" icon.
function BotAvatar() {
  return (
    <div className={css.chatBotAvatar}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 18 18"
        fill="none"
      >
        <path
          d="M9.71757 0.555288L12.0974 5.67187L17.214 8.05168C17.5313 8.21033 17.7692 8.52762 17.7692 8.88462C17.7692 9.28129 17.5313 9.5986 17.214 9.75717L12.0974 12.1371L9.71757 17.2536C9.55892 17.5709 9.24161 17.7692 8.88462 17.7692C8.48794 17.7692 8.17067 17.5709 8.01202 17.2536L5.63221 12.1371L0.515625 9.75717C0.198317 9.5986 0 9.28129 0 8.88462C0 8.52762 0.198317 8.21033 0.515625 8.05168L5.63221 5.67187L8.01202 0.555288C8.17067 0.237981 8.48794 0 8.88462 0C9.24161 0 9.55892 0.237981 9.71757 0.555288Z"
          fill="white"
        />
      </svg>
    </div>
  );
}

function LoadingDots() {
  return (
    <div className={css.chatMessageRow}>
      <BotAvatar />
      <div className={css.chatLoadingBubble}>
        {[0, 1, 2].map((i) => (
          <span key={i} className={css.chatLoadingDot} style={{ animationDelay: `${i * 0.2}s` }} />
        ))}
      </div>
    </div>
  );
}

function VoiceTranscriptActivity({ transcript }) {
  if (!transcript) return null;

  return (
    <div className={css.voiceActivity}>
      <div className={classNames(css.chatMessageRow, css.chatMessageRowUser)}>
        <div className={classNames(css.chatBubble, css.chatBubbleUser, css.voiceTranscriptBubble)}>
          {transcript}
        </div>
      </div>
    </div>
  );
}

// A failure renders as an ordinary assistant turn — avatar plus bubble, in the
// message stream directly under the user's prompt — not as a panel-level
// banner. The bubble's two children (the text block, and the optional CTA)
// carry the design's 10px gap; title and body sit inside one block so no gap
// opens between them.
//
// aria-live so assistive tech announces the failure. The PRD also asks for a
// spoken equivalent when Voice Search is active — that belongs to S6
// (ATAI-140), which isn't built yet, so there is nothing to hook into here.
function ErrorMessage({ card, onRetry }) {
  return (
    <div className={css.chatMessageRow} role="alert" aria-live="assertive">
      <BotAvatar />
      <AIChatErrorCard
        title={card.title}
        body={card.body}
        retry={card.retry}
        onRetry={onRetry}
      />
    </div>
  );
}

const UNAVAILABLE_MODAL_TITLE_ID = 'ai-search-unavailable-title';

// Step 1's state is a modal rather than an in-stream bubble: the chat panel
// itself may hold nothing to attach a message to when the service was never
// reached.
//
// Deliberately not `components/base/Modal`: that component carries no focus
// management of its own, so it would not close the a11y gap this handles, and
// it does add ESC dismissal, outside-click dismissal and a close X — the three
// affordances this forced-choice state omits (the Figma frame has no close
// icon; both ways out are explicit buttons). Focus is managed here instead:
// initial focus on the primary action, Tab cycled between the two buttons, and
// the caller's focus restored on close.
function UnavailableModal({ onRetry, onUseLocationSearch }) {
  const secondaryRef = useRef(null);
  const primaryRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    primaryRef.current?.focus();
    return () => {
      // Focus would otherwise be left on a removed node, dropping the keyboard
      // user back at the top of the document.
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  // Only two focusable elements, so the trap is a two-way cycle rather than a
  // general focusable-node query over the subtree.
  function handleKeyDown(e) {
    if (e.key !== 'Tab') return;
    const first = secondaryRef.current;
    const last = primaryRef.current;
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      // backwards off the first control — wrap to the last
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      // forwards off the last control — wrap to the first
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className={css.chatModalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={UNAVAILABLE_MODAL_TITLE_ID}
      onKeyDown={handleKeyDown}
    >
      <div className={css.chatModal}>
        <div className={css.chatModalInner}>
          {/* The design opens with an illustration (robot + alert badge) —
              the `Group` node at 296.83×155.95, order 0 inside Frame 11. */}
          <img src={UnavailableIllustration} alt="" className={css.chatModalIllustration} />
          <div className={css.chatModalTitle} id={UNAVAILABLE_MODAL_TITLE_ID}>
            AI Search is temporarily unavailable
          </div>
          <div className={css.chatModalBody}>
            We&apos;re having trouble connecting to AI Search right now. Please try again in a
            moment, or switch to Location Search to continue searching.
          </div>
          <div className={css.chatModalActions}>
            <button
              type="button"
              className={css.chatModalSecondary}
              onClick={onUseLocationSearch}
              ref={secondaryRef}
            >
              Use Location Search
            </button>
            <button
              type="button"
              className={css.chatModalPrimary}
              onClick={onRetry}
              ref={primaryRef}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Shared by the card's chips (formatFilterChip) and the localFilter chip
// records dispatched for the Filters-button badge — same "what does this
// filter/value actually mean" resolution both places. listingType's stored
// value is a short code ('P', 'V', 'SBTW1', ...), not a display string —
// resolve it through LISTING_TYPE_LABELS instead of showing the field label.
function chipDisplayName(key, value) {
  const valueLabel = filterValueLabel(key, value);
  if (valueLabel) return valueLabel;
  if (key === 'listingType' && typeof value === 'string') {
    return LISTING_TYPE_LABELS.get(value) || filterFieldLabel(key);
  }
  return filterFieldLabel(key);
}

// Plain-value display for a validated filter value — a date (now a moment,
// per validateFilterValue) formatted rather than shown as a verbose
// toString(), an array joined, everything else as-is.
function formatFilterValue(value) {
  if (moment.isMoment(value)) return value.format('MM/DD/YYYY');
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function toWholePercent(value) {
  return Math.round(Number(value) * 10000) / 100;
}

function toModelFilters(filters) {
  if (!filters || typeof filters !== 'object') return filters;
  return Object.keys(filters).reduce((acc, key) => {
    const value = filters[key];
    const isPercent = getField(key)?.type === 'percent' && typeof value === 'number';
    acc[key] = isPercent ? toWholePercent(value) : value;
    return acc;
  }, {});
}

function formatFilterChip(key, value) {
  const name = chipDisplayName(key, value);
  // A resolved value (vacant/rental's true "Vacant"/"For Rent", a resolved
  // listingType category, or any plain true boolean) is shown bare — no
  // "Field: value" prefix, since the name alone already says what's being
  // searched for.
  const isResolvedValue =
    filterValueLabel(key, value) != null || (key === 'listingType' && typeof value === 'string');
  if (isResolvedValue || value === true) {
    return name;
  }
  if (getField(key)?.type === 'percent' && typeof value === 'number') {
    return `${name}: ${toWholePercent(value)}%`;
  }
  return `${name}: ${formatFilterValue(value)}`;
}

// The real localFilter chip record shape (e.g. PreForeclosureBank.js:
// {key, id:<human label>, name:<value>, apiKey, removeID}) — AppliedFilterSection.js
// renders `${filterKey.id}: ${filterKey.name}`, so `id` must be the field
// label and `name` the value, not the other way around (a prior version of
// this had them swapped, rendering e.g. "bedroomsMin: Bedrooms Min" instead
// of "Bedrooms Min: 3" — Fahim's PR #3344 review round 4, MAJOR).
function chipRecordValue(key, value) {
  const resolved = chipDisplayName(key, value);
  const isResolvedValue =
    filterValueLabel(key, value) != null || (key === 'listingType' && typeof value === 'string');
  if (isResolvedValue) {
    // vacant/rental's resolved true-meaning, or a listingType category
    return resolved;
  }
  if (typeof value === 'boolean') {
    // plain boolean field — the label already names it, so just yes/no
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value) || isListFilter(key)) {
    // multi-select ('list') field — resolve each raw code to its display
    // label, so the left-panel chip reads "Active, Coming Soon" rather than
    // the raw "ACTIVE, COMING_SOON" formatFilterValue's bare join produced
    // (ATAI-220 — same defect as the summary card's chips). isListFilter
    // covers a single code sent as a bare string, which is not an array.
    return filterValueLabels(key, value).join(', ');
  }
  // everything else — date, number, plain string
  return formatFilterValue(value);
}

function toStoredFilters(validatedFilters) {
  return Object.keys(validatedFilters).reduce((acc, key) => {
    const value = validatedFilters[key];
    acc[key] = Array.isArray(value) ? value.join(',') : value;
    return acc;
  }, {});
}

// Shared by the live apply_property_search_filters tool path and the
// Continue-path of the S7 "Welcome back" resume flow (ATAI-155) — same
// redux dispatch sequence either way: replace/merge filters, rebuild the
// localFilter chip records (reusing the existing per-category UI — see
// filterCategoryKey's comment — not a bespoke one), keep the Lead Lists
// tile's active highlight (favoriteSelectedName) in sync, and set the
// resolved location. Extracted here rather than duplicated once a second
// caller (Continue) needed the exact same sequence.
// ATAI-279: returns the filters that were *actually* applied — this turn's
// requested keys minus anything the reducer's whitelist dropped — so every
// surface that reports the search can be built from what happened rather than
// from what was asked for. `constrainObject` drops an unknown key silently, no
// error and no return value (ATAI-246), so redux read back after the dispatch
// is the only honest answer to "what got applied".
function applyResolvedSearch(
  dispatch,
  store,
  { validatedFilters, effectiveLocation, clearExisting = true }
) {
  if (clearExisting !== false) {
    dispatch(clearFilter());
    dispatch(clearLocalFilter());
  }
  let appliedFilters = {};
  const requestedKeys = Object.keys(validatedFilters || {});
  if (requestedKeys.length) {
    // Dispatched on its own, before anything is reported, so the surviving set
    // can be read back. Redux dispatch is synchronous, so the very next
    // getState() already reflects it.
    dispatch(updateFilter(toStoredFilters(validatedFilters)));
    // Intersect rather than taking the stored filter wholesale: `setFilter`
    // folds `defaultFilter` into every update, so redux always carries
    // `estimatedValueGrowthPeriod: 'ONE_MONTH'` even when nobody asked for it —
    // reporting that verbatim would *invent* a chip while removing another.
    // Keep this turn's own keys, minus whatever the whitelist dropped, and keep
    // the validated values so chip rendering is byte-for-byte unchanged.
    const storedFilter = selectFilter(store.getState())?.toJS() || {};
    appliedFilters = requestedKeys.reduce((acc, key) => {
      if (Object.prototype.hasOwnProperty.call(storedFilter, key)) {
        acc[key] = validatedFilters[key];
      }
      return acc;
    }, {});
  }
  const appliedKeys = Object.keys(appliedFilters);
  if (appliedKeys.length) {
    const records = [];
    appliedKeys.forEach((key) => {
      const base = /^(.*)(Min|Max)$/.exec(key)?.[1];
      if (!base) {
        records.push({
          key: filterCategoryKey(key),
          id: filterFieldLabel(key),
          name: chipRecordValue(key, appliedFilters[key]),
          apiKey: key,
          removeID: key,
        });
        return;
      }
      if (records.some((record) => record.apiKey === base)) return;
      const min = appliedFilters[`${base}Min`];
      const max = appliedFilters[`${base}Max`];
      const name =
        moment.isMoment(min) || moment.isMoment(max)
          ? generateMinMaxDateValue(min, max)
          : generateMinMaxValue(min, max);
      if (!name) return;
      records.push({
        key: filterCategoryKey(base),
        id: filterFieldLabel(base),
        name,
        apiKey: base,
        removeID: base,
        showALLDisplayValue: name,
      });
    });
    dispatch(updateLocalFilter(records));
  }
  if (appliedFilters.listingType) {
    const matchedListingOption = ListingTypeOptions.find(
      (option) => option.value === appliedFilters.listingType
    );
    if (matchedListingOption) dispatch(setFavoriteItem(matchedListingOption));
  } else if (clearExisting !== false) {
    dispatch(setFavoriteItem(null));
  }
  dispatch(setSuggestionValue(effectiveLocation));
  // PROPS-3641: setSuggestionValue also sets the shared suggestion *label*
  // (data/search/reducer.js's setSuggestionValue helper derives it from
  // effectiveLocation.label) — the same redux field the AI Search input's
  // `value` prop reads (Header/index.js). handleAiSearchSubmit already
  // clears it at submit time via clearSuggestion(), but this dispatch runs
  // later, after the tool round-trip resolves, re-filling it with the
  // searched city/state right as the result comes back — instead of the
  // input going back to its placeholder. Blank the label back out
  // (leaving the value itself — e.g. cityId/countyId — intact for whatever
  // else reads it) rather than making the input's value prop mode-aware.
  dispatch(setSuggestionLabel(''));
  return appliedFilters;
}

// "Applied Filter Summary" card (Story 5) — matches Figma "AI Search - LONG
// CHAT" node 284:29787 exactly (colors/layout/icons): a periwinkle header
// bar ("SEARCH FILTERS"), the AI-generated summary sentence, a location
// line, filter chips, and a footer with an "Applied to search" status.
// Applying is automatic the moment the tool_use arrives (see the TOOLS
// comment/runConversationLoop), so this card only ever renders
// already-applied — a missing-location guard failure never reaches here at
// all (it's a plain assistant message instead, since nothing was actually
// applied). No Undo — cut for v1 per the design's own scope decision.
// A multi-select ('list') filter carries up to 13 values (all 12 MLS statuses,
// all 13 tax exemption statuses). Joining those into ONE chip produced a single
// ~150-character string inside a pill fixed at 31px, whose text spilled out over
// the location line above, the neighbouring chip, and the "Applied to search"
// footer — ATAI-220. Rendered as a group instead: the field label once, then one
// normal-size chip per value, which wraps like every other chip.
function filterChipGroups(filters) {
  return Object.keys(filters || {}).map((key) => {
    const value = filters[key];
    if (Array.isArray(value) || isListFilter(key)) {
      // multi-select — field label row, one chip per resolved value. Keys off
      // the field's type as well as the value's shape so a single code sent as
      // a bare string resolves too, instead of falling through to
      // formatFilterChip and rendering raw (PR #3461 review, nit 3).
      return { key, label: filterFieldLabel(key), chips: filterValueLabels(key, value) };
    }
    // single-value — one self-describing chip, no label row
    return { key, label: null, chips: [formatFilterChip(key, value)] };
  });
}

function FilterSummaryCard({ card }) {
  const groups = filterChipGroups(card.filters);
  return (
    <div className={css.chatMessageRow}>
      <BotAvatar />
      <div className={css.aiFilterCard}>
        <div className={css.aiFilterCardHeader}>
          <img src={aiFilterCardHeaderIcon} alt="" width="14" height="12" />
          <span className={css.aiFilterCardHeaderTitle}>SEARCH FILTERS</span>
        </div>
        <div className={css.aiFilterCardBody}>
          <div className={css.aiFilterCardSummary}>{card.summary}</div>
          {card.location?.label && (
            <div className={css.aiFilterCardLocation}>
              <img src={aiFilterCardLocationPin} alt="" width="11" height="20" />
              <span>{card.location.label}</span>
            </div>
          )}
          {groups.length > 0 && (
            <div className={css.aiFilterCardChipGroups}>
              {groups.map((group) => (
                <div key={group.key} className={css.aiFilterCardChipGroup}>
                  {group.label && (
                    <div className={css.aiFilterCardChipGroupLabel}>{group.label}</div>
                  )}
                  <div className={css.aiFilterCardChips}>
                    {group.chips.map((chip, index) => (
                      // index in the key as well as the label: two raw codes in
                      // one field can humanize to the same display string
                      <span key={`${chip}-${index}`} className={css.aiFilterCardChip}>
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className={css.aiFilterCardFooter}>
            <div className={css.aiFilterCardAppliedStatus}>
              <img src={aiFilterCardCheck} alt="" width="20" height="20" />
              <span>Applied to search</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// "Welcome back" resume card (Story 7 §2, ATAI-155) — offered only for a
// snapshot that already passed re-validation (schema version, Story 5's
// value validation, and the supported-location guard — see buildResumeState),
// so this component itself trusts card.resolvedFilters/resolvedLocation
// without re-checking them. Reuses the same visual language as
// FilterSummaryCard (periwinkle header, location pin) rather than a new
// style, since it's the same "search state" concept — just before vs. after
// the user's continue/fresh choice. `resolved`/`dismissed` swap the footer
// for a static status line once the user picks, so the buttons can't be
// clicked twice (e.g. a second "Continue" while the first is still applying).
function WelcomeBackCard({ card, onContinueSearch, onStartFresh }) {
  return (
    <div className={css.chatMessageRow}>
      <BotAvatar />
      <div className={css.aiFilterCard}>
        <div className={css.aiFilterCardHeader}>
          <img src={aiFilterCardHeaderIcon} alt="" width="14" height="12" />
          <span className={css.aiFilterCardHeaderTitle}>WELCOME BACK</span>
        </div>
        <div className={css.aiFilterCardBody}>
          <div className={css.aiFilterCardSummary}>
            Would you like to continue your search for {card.summary}?
          </div>
          {card.locationLabel && (
            <div className={css.aiFilterCardLocation}>
              <img src={aiFilterCardLocationPin} alt="" width="11" height="20" />
              <span>{card.locationLabel}</span>
            </div>
          )}
          {card.resolved || card.dismissed ? (
            <div className={css.aiFilterCardFooter}>
              <div className={css.aiFilterCardAppliedStatus}>
                <img src={aiFilterCardCheck} alt="" width="20" height="20" />
                <span>{card.resolved ? 'Continued search' : 'Started a new search'}</span>
              </div>
            </div>
          ) : (
            <div className={css.welcomeBackActions}>
              <button
                type="button"
                className={css.welcomeBackBtnPrimary}
                onClick={() => onContinueSearch(card)}
              >
                Continue Search
              </button>
              <button
                type="button"
                className={css.welcomeBackBtnSecondary}
                onClick={() => onStartFresh(card)}
              >
                Start Fresh
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageItem({ msg, onContinueSearch, onStartFresh }) {
  if (msg.role === 'filter_card') {
    return <FilterSummaryCard card={msg} />;
  }
  // Panel-authored progress note, not a model turn — never enters `history`,
  // so it can't be mistaken by the model for something it said.
  if (msg.role === 'status_note') {
    return <div className={css.chatStatusNote}>{msg.content}</div>;
  }
  if (msg.role === 'welcome_back') {
    return (
      <WelcomeBackCard card={msg} onContinueSearch={onContinueSearch} onStartFresh={onStartFresh} />
    );
  }
  // The streaming placeholder before its first token: the loading dots below
  // the stream already stand in for it, so rendering an empty bubble here puts
  // a second, blank bubble above them (ATAI-207).
  if (!msg.content) {
    return null;
  }
  const isUser = msg.role === 'user';
  return (
    <div className={classNames(css.chatMessageRow, { [css.chatMessageRowUser]: isUser })}>
      {!isUser && <BotAvatar />}
      <div className={classNames(css.chatBubble, isUser ? css.chatBubbleUser : css.chatBubbleBot)}>
        <MessageText text={msg.content} />
      </div>
    </div>
  );
}

// Normalized form for dropping streamed lines that exactly repeat an option.
function clarifyingTextKey(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/[*_`]/g, '')
    .replace(/^\s*(?:\d+[.)]|[-•*])\s*/, '')
    .replace(/\s+/g, ' ')
    .replace(/[\s.,:;!?]+$/, '')
    .trim();
}

// ask_clarifying_question renders as a plain formatted message — lead-in text
// plus a numbered list of options (Figma "AI Search - LONG CHAT", node
// 284:29769) — no clickable picker; the user replies in natural language via
// the AI Search bar, same as any other turn (per the PRD-confirmed decision
// that a bespoke interactive message type isn't required).
function formatClarifyingQuestion(leadingText, question, options) {
  const optionList = (options || []).filter(Boolean);
  const optionKeys = new Set(optionList.map(clarifyingTextKey).filter(Boolean));
  const optionLines = optionList.map((option, i) => `${i + 1}. ${option}`).join('\n');

  const leadIn = String(leadingText ?? '')
    .split('\n')
    .filter((line) => {
      const key = clarifyingTextKey(line);
      return !key || !optionKeys.has(key);
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const questionKey = clarifyingTextKey(question);
  const leadInKey = clarifyingTextKey(leadIn);
  const leadInAlreadyAsks =
    Boolean(leadIn) && (leadIn.includes('?') || (questionKey && leadInKey.includes(questionKey)));

  return [leadIn, leadInAlreadyAsks ? '' : question, optionLines].filter(Boolean).join('\n\n');
}

// The location the conversation last actually applied, keyed by the session it
// belongs to. Module scope rather than a ref, because the panel is keyed on
// chatResetToken (Header/index.js) and remounts on every search-mode switch —
// which, unlike Clear All, keeps the conversation and its session id. A ref
// dies with the mount, so a there-and-back mode switch used to drop this
// memory while redux's own location was a property view's leftovers, leaving
// the next follow-up filter with nothing to search (ATAI-200). Clear All and
// Start Fresh both rotate the session id, so the entry either leaves behind is
// never read again — only the one entry is kept, so this cannot grow.
let lastAppliedLocationEntry = null;

function readLastAppliedLocation(sessionId) {
  if (lastAppliedLocationEntry?.sessionId !== sessionId) {
    // a different conversation's location, or none recorded yet
    return null;
  }
  return lastAppliedLocationEntry.location;
}

function writeLastAppliedLocation(sessionId, location) {
  lastAppliedLocationEntry = { sessionId, location };
}

export default function SearchChatPanel({
  open,
  initialPrompt,
  onConsumeInitialPrompt,
  context,
  anchorRef,
  dividerRef,
  onLoadingChange,
  onSubmitError,
  onAssistantResponseComplete,
  onChatContentChange,
  voiceTranscript,
  voiceActive,
  onUseLocationSearch,
}) {
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  // The active failure state, as a cause from AgentErrorCause or TOO_LONG.
  const [errorCause, setErrorCause] = useState(null);
  const [activeStreamId, setActiveStreamId] = useState(null);
  // Shows dots while the voice-mode history/resume fetch is pending.
  const [historyLoading, setHistoryLoading] = useState(false);
  // top/left/width, measured off anchorRef/dividerRef (see the layout effect
  // below) rather than computed inline during render. Reading
  // getBoundingClientRect() straight in the render body reflects the *last
  // committed* DOM, one render behind whatever this same render is about to
  // change (e.g. toggling `open` via the expand icon flips a class on the
  // search box in the very same commit) — that stale read is exactly what
  // made the panel jump right on open/close, correcting itself only once a
  // *later* render (typing, then Enter) came around to measure again.
  const [positionStyle, setPositionStyle] = useState(undefined);

  // Sets the error card, its spoken equivalent, and the onSubmitError banner.
  // onAssistantResponseComplete already no-ops for a non-voice turn (index.js
  // only speaks when shouldSpeakNextAiResponse is set), so this is safe to
  // call unconditionally for every error/rate-limit/truncation card — S8
  // requires a spoken equivalent whenever Voice Search is active, and
  // onAssistantResponseComplete was previously only ever invoked on a
  // successful reply. onSubmitError was accepted as a prop but never invoked
  // — index.js wires it to a banner that stays visible even while the panel
  // is collapsed, so it's called here too as a second, redundant surface.
  const setErrorCauseAndSpeak = (cause) => {
    setErrorCause(cause);
    const card = errorCardFor(cause);
    const message = [card.title, card.body].filter(Boolean).join('. ');
    onAssistantResponseComplete?.(message);
    onSubmitError?.(message);
  };
  const messagesEndRef = useRef(null);
  const sessionIdRef = useRef(getOrCreatePersistentSessionId());
  const lastPromptRef = useRef(null);
  // ATAI-286: whether the user message now being answered applied a search.
  // Reset in handleSend, set once an apply's results land; a turn that ends
  // with it still false marks the results header's search as the earlier one.
  const appliedThisTurnRef = useRef(false);
  // The conversation's own memory of "where we're searching" lives at module
  // scope, keyed by session id (see readLastAppliedLocation) rather than in a
  // ref that a remount would clear. It is owned by the panel rather than read
  // back out of redux (ATAI-200).
  //
  // `context.currentLocation` is `state.data.search.location`
  // (Header/index.js's mapStateToProps), and SET_LOCATION replaces that whole
  // object rather than merging into it (data/search/reducer.js) — so any
  // unrelated Search flow that writes a partial location drops the geo ids the
  // conversation resolved. Opening a property from the results grid
  // (Search/index.js's handleNearbyClick) and coming back out of it
  // (handleBackFromProperty) both do exactly that: they write
  // `{mode, query: {...}}`, leaving cityId/countyId/... only under `query`,
  // while `state.data.search.filter` is left untouched.
  //
  // Confirmed live in chat_conversation_logs (session
  // search-077a44ac-3725-40ba-ab69-89abbaa2ba16): an apply resolved
  // "Los Angeles, CA" and returned 111,645 results, and three turns later the
  // next apply — adding only Year Built — hit the missing-location guard below
  // and made the agent ask the user to re-confirm a location it had already
  // resolved. A later turn in the same session shows the asymmetry directly:
  // the agent still listed the active filters while offering "Location" as one
  // the user had yet to supply.
  const dispatch = useDispatch();
  const store = useStore();

  // Latest loading/stale/resultCount, readable from inside an async handler
  // (applying a filter card) without re-subscribing — useSelector's return
  // value is only fresh at render time, but the wait-for-results poll below
  // runs across many ticks after the triggering click.
  const searchLoading = useSelector(selectLoading);
  const searchStale = useSelector(selectStale);
  const activeContext = useSelector(selectActiveContext);
  const listingResultVersion = useSelector(selectListingResultVersion);
  const listingError = useSelector(selectListingError);
  const searchLoadingRef = useRef(searchLoading);
  const searchStaleRef = useRef(searchStale);
  const resultCountRef = useRef(activeContext?.get('resultCount'));
  // The one signal that says "the count sitting in the active context came
  // from the search we just triggered": data/search bumps it on
  // SEARCH_LISTING_SUCCESS and nowhere else. Watching `loading` instead (what
  // this used to do) can't see the difference between a search that returned
  // and one that failed or timed out — both drop loading to false, and the
  // count left behind is then either the previous search's or the 0 that
  // applyResolvedSearch's clearFilter() just wrote (ATAI-192).
  const listingResultVersionRef = useRef(listingResultVersion);
  const listingErrorRef = useRef(listingError);
  useEffect(() => {
    searchLoadingRef.current = searchLoading;
  }, [searchLoading]);
  useEffect(() => {
    searchStaleRef.current = searchStale;
  }, [searchStale]);
  useEffect(() => {
    listingResultVersionRef.current = listingResultVersion;
  }, [listingResultVersion]);
  useEffect(() => {
    listingErrorRef.current = listingError;
  }, [listingError]);
  useEffect(() => {
    resultCountRef.current = activeContext?.get('resultCount');
  }, [activeContext]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Derived from live rects (not hardcoded px) so the panel stays flush with
  // the search box across any viewport size. Starts from the
  // searchModeDivider (skipping the Location/AI Search toggle) rather than
  // the search box's own left edge, per Figma, but still ends flush with the
  // search box's right edge.
  const measurePosition = useCallback(() => {
    const anchorRect = anchorRef?.current?.getBoundingClientRect();
    if (!anchorRect) {
      setPositionStyle(undefined);
      return;
    }
    const dividerRect = dividerRef?.current?.getBoundingClientRect();
    const panelLeft = dividerRect?.left ?? anchorRect.left;
    setPositionStyle({
      position: 'fixed',
      // -1px so the panel's top border overlaps the search box's bottom
      // border (both 1px) instead of sitting flush beneath it — otherwise
      // the two borders render as a visible double line.
      top: `${anchorRect.bottom - 1}px`,
      left: `${panelLeft}px`,
      width: `${anchorRect.right - panelLeft}px`,
    });
  }, [anchorRef, dividerRef]);

  // useLayoutEffect (not inline in render, not plain useEffect) so this
  // remeasures synchronously right after each commit — including the very
  // commit that flips `open` — and before the browser paints, instead of
  // one render behind it.
  useLayoutEffect(() => {
    measurePosition();
  }, [open, measurePosition]);

  // Keeps it in sync across breakpoints/window resizes too, while open —
  // a plain resize with no other state change never triggers a re-render on
  // its own.
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('resize', measurePosition);
    window.addEventListener('orientationchange', measurePosition);
    return () => {
      window.removeEventListener('resize', measurePosition);
      window.removeEventListener('orientationchange', measurePosition);
    };
  }, [open, measurePosition]);

  // Also remeasures on any border-box size change of the search box itself
  // — the case a window resize never fires for. Focusing the AI Search
  // input while typing adds the 2px active/periwinkle border
  // (searchAiFocus:focus-within, Header/style.scss); pressing Enter blurs
  // it, dropping back to the plain 1px border, which shifts the box's real
  // width/right edge without the window resizing or `open` changing. Without
  // this, the panel stayed sized to the *focused* box after Enter instead of
  // snapping back to match it.
  useEffect(() => {
    if (!open || typeof ResizeObserver === 'undefined') return undefined;
    const anchorEl = anchorRef?.current;
    const dividerEl = dividerRef?.current;
    if (!anchorEl) return undefined;
    const observer = new ResizeObserver(measurePosition);
    observer.observe(anchorEl);
    if (dividerEl) observer.observe(dividerEl);
    return () => observer.disconnect();
  }, [open, anchorRef, dividerRef, measurePosition]);

  useEffect(() => {
    if (initialPrompt && handleSend(initialPrompt)) {
      onConsumeInitialPrompt?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, loading]);

  // Story 7 §2/§4 (ATAI-155): fetch the last applied search-state snapshot
  // once, on the first mount that has `open` true, keyed by the persistent
  // session id above. SearchChatPanel is mounted unconditionally in the
  // header regardless of whether the AI panel is ever opened (Header/
  // index.js), so firing this on every mount hit GET /chat/history on every
  // Search page load for users who never open the panel — the
  // highest-traffic page in the app (PR #3361 review, MAJOR). Gating on the
  // first `open === true` transition still populates the panel before the
  // user sees any content, and skips the fetch entirely for everyone else.
  // historyFetchedRef (not state) so this can't re-fire on a later
  // open/close toggle of the same mounted instance.
  //
  // The prior transcript itself is deliberately NOT rendered here — only
  // the Welcome Back card is. Showing the full old conversation on every
  // reload, before the user has said they want it back, is a "replay"
  // rather than the "welcome back, continue or start fresh?" prompt the
  // product spec actually calls for. The fetched raw messages are cached in
  // fetchedHistoryMessagesRef so handleContinueSearch can restore the
  // transcript at the moment the user actually asks for it, with no second
  // network call.
  //
  // Starting voice mode is an intentional AI Search entry point even before
  // the user expands the panel, so begin the same history load there. If the
  // panel opens before the response lands, the loading dots below cover the
  // otherwise empty state.
  const historyFetchedRef = useRef(false);
  const fetchedHistoryMessagesRef = useRef(null);
  const userSubmittedRef = useRef(false);
  // The effect reruns as voice/open changes, so cancellation only belongs to unmount.
  const historyCancelledRef = useRef(false);
  useEffect(
    () => () => {
      historyCancelledRef.current = true;
    },
    []
  );
  useEffect(() => {
    if ((!open && !voiceActive) || historyFetchedRef.current) return;
    historyFetchedRef.current = true;

    setHistoryLoading(true);
    (async () => {
      try {
        const data = await fetchSearchAgentChatHistory(sessionIdRef.current);
        if (historyCancelledRef.current) return;
        fetchedHistoryMessagesRef.current = data?.messages || null;

        const resume = await buildResumeState(data?.searchSnapshot);
        if (historyCancelledRef.current || !resume || userSubmittedRef.current) return;
        setMessages((prev) => [
          ...prev,
          { id: `${Date.now()}_welcome`, role: 'welcome_back', ...resume },
        ]);
      } catch (err) {
        // History/resume is a nice-to-have on mount, not load-bearing — a
        // failed fetch just leaves the panel starting empty, same as every
        // session before this story existed.
      } finally {
        if (!historyCancelledRef.current) setHistoryLoading(false);
      }
    })();
  }, [open, voiceActive]);

  useEffect(() => {
    const hasDisplayableContent =
      Boolean(errorCause) ||
      messages.some((msg) => {
        if (msg.role === 'welcome_back') return !msg.dismissed;
        if (msg.role === 'user') return Boolean(msg.content);
        if (msg.role === 'assistant') return Boolean(msg.content);
        return msg.role === 'filter_card';
      });
    const emptyHistoryChecked =
      historyFetchedRef.current && !historyLoading && !loading && messages.length === 0;

    if (hasDisplayableContent || emptyHistoryChecked) {
      setPersistentSearchChatContent(hasDisplayableContent);
      onChatContentChange?.(hasDisplayableContent);
    }
  }, [errorCause, historyLoading, loading, messages, onChatContentChange]);

  // Continue path (Story 7 §2): applies the already-re-validated snapshot
  // directly via the same redux dispatch sequence a live tool apply uses —
  // no tool replay, no model round-trip (buildResumeState already did the
  // re-resolution/validation before this card was ever shown). Restoring
  // the actual prior transcript is deferred to exactly this moment — the
  // user explicitly asked to continue — using the messages already fetched
  // on mount (fetchedHistoryMessagesRef), so no second /chat/history call.
  async function handleContinueSearch(card) {
    const priorTurns = buildPriorTurns(fetchedHistoryMessagesRef.current);
    setMessages((prev) => {
      const withResolved = prev.map((m) => (m.id === card.id ? { ...m, resolved: true } : m));
      const cardIndex = withResolved.findIndex((m) => m.id === card.id);
      return [...withResolved.slice(0, cardIndex), ...priorTurns, ...withResolved.slice(cardIndex)];
    });
    setHistory((prev) => [
      ...priorTurns.map((m) => ({ role: m.role, content: m.content })),
      ...prev,
    ]);
    const appliedFilters = applyResolvedSearch(dispatch, store, {
      validatedFilters: card.resolvedFilters,
      effectiveLocation: card.resolvedLocation,
      clearExisting: true,
    });
    // A resumed search is on the same footing as a live apply — buildResumeState
    // re-resolved and re-validated this location before the card was ever
    // offered — so the resumed conversation gets the same location memory
    // (ATAI-200); without it, the first follow-up filter after "Continue
    // Search" would hit the missing-location guard.
    writeLastAppliedLocation(sessionIdRef.current, card.resolvedLocation);
    // ATAI-286: the resumed search is now the one on screen. Set before the
    // wait (same reason as the live apply), with the snapshot's own summary,
    // not the card's 'your last search' display fallback.
    dispatch(setAiSearchLabel(card.labelSummary));
    const { status: resultStatus, count: resultCount } = await waitForSearchResults();
    // Same split outcome as a live apply: the snapshot's filters are on the
    // search, the count may be unknown (ATAI-192).
    if (resultStatus !== SearchResultStatus.CONFIRMED) {
      setErrorCause(SEARCH_TIMED_OUT);
    }
    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}_card`,
        role: 'filter_card',
        summary: card.summary,
        // ATAI-279: what the reducer kept, not what the snapshot asked for — a
        // snapshot written before this fix can still carry a phantom key.
        filters: appliedFilters,
        location: card.resolvedLocation,
        resultCount,
      },
    ]);
  }

  // Both acceptance-criteria halves of "let the user continue or start
  // fresh" need to actually happen here: rotate the session id (so the old
  // conversation stops being resumable) and drop any cached transcript,
  // instead of only flipping `dismissed` on the card itself.
  function handleStartFresh(card) {
    resetPersistentSearchSession();
    sessionIdRef.current = getOrCreatePersistentSessionId();
    fetchedHistoryMessagesRef.current = null;
    // Starting fresh drops the conversation's location memory too (ATAI-200) —
    // the new conversation resolves its own location rather than inheriting the
    // abandoned one.
    writeLastAppliedLocation(sessionIdRef.current, null);
    // ...and whichever search it last labelled on the results header (ATAI-286).
    dispatch(clearAiSearchLabel());
    onChatContentChange?.(false);
    setMessages([{ ...card, dismissed: true }]);
    setHistory([]);
  }

  // Streams one turn against the given history. Resolves after the turn is
  // fully handled — including, for resolve_location, a silent recursive
  // continuation once the tool_result comes back (single-match auto-resolve
  // per the design: the user never sees the round-trip, only its outcome).
  async function runConversationLoop(
    turnHistory,
    resolveLocationDepth = 0,
    applyDepth = 0,
    extraContext = null
  ) {
    const streamMsgId = `${Date.now()}_a`;
    setActiveStreamId(streamMsgId);
    setMessages((prev) => [...prev, { id: streamMsgId, role: 'assistant', content: '' }]);

    let fullText = '';
    let renderedLength = 0;
    let renderTimer = null;
    let streamDone = false;
    let toolUse = null;
    let sawAnyToolUse = false;
    let errorText = null;

    function startRenderLoop() {
      if (renderTimer) return;
      renderTimer = setInterval(() => {
        if (renderedLength < fullText.length) {
          renderedLength = Math.min(renderedLength + STREAM_CHARS_PER_TICK, fullText.length);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamMsgId ? { ...m, content: fullText.slice(0, renderedLength) } : m
            )
          );
        } else if (streamDone) {
          clearInterval(renderTimer);
          renderTimer = null;
        }
      }, STREAM_RENDER_INTERVAL_MS);
    }

    // What the model is *told* the search location is — the other half of
    // ATAI-200. `context.currentLocation` is redux's `search.location`, and
    // SET_LOCATION replaces that object wholesale (data/search/reducer.js)
    // with payloads that carry no top-level label or geo id: opening a
    // property from the results grid (Search/index.js's handleNearbyClick)
    // and coming back out of it (handleBackFromProperty) both write
    // `{mode, query: {...}, stale, backBtnFlag}`. The prompt builder renders
    // whatever arrives verbatim (PropertySearchPromptBuilder's
    // buildDynamicContextText: `currentLocation=` + the object, or `none`
    // only when it is null), so the model is handed
    // `currentLocation={mode=PROPERTY, query={...}}` and can answer "I don't
    // have a location set yet" *without* calling
    // apply_property_search_filters — a turn the apply-side fallback chain
    // below never runs on. Confirmed on QA (2026-09-15) by dispatching that
    // payload mid-conversation: the next request carried exactly that shape.
    // So the conversation's own memory has to reach the model too, not just
    // the apply path, and an unusable shape is sent as null rather than left
    // for the model to interpret.
    const mergedContext = extraContext ? { ...context, ...extraContext } : context;
    let requestLocation;
    if (hasSupportedLocation(mergedContext?.currentLocation)) {
      // redux still holds the location this conversation is searching
      requestLocation = mergedContext.currentLocation;
    } else {
      // redux was overwritten by an unrelated Search flow — fall back to what
      // this conversation last applied (null until it has applied anything)
      requestLocation = readLastAppliedLocation(sessionIdRef.current);
    }

    try {
      await streamSearchAgentChat({
        history: turnHistory,
        context: {
          ...mergedContext,
          currentLocation: requestLocation,
          currentFilters: toModelFilters(mergedContext?.currentFilters),
        },
        sessionId: sessionIdRef.current,
        tools: TOOLS,
        onText: (chunk) => {
          fullText += chunk;
          startRenderLoop();
        },
        // ask_clarifying_question/resolve_location/apply_property_search_filters
        // handled below once the stream ends. Voice (S6) and error-state (S8)
        // tool calls aren't this story's scope. Per S2's defensive fallback,
        // if the model emits 2+ tool_use blocks in one turn despite the
        // instruction not to, keep only the first — regardless of name, so a
        // future tool ahead of one of ours in the same turn doesn't get
        // silently skipped over.
        // Dispatcher hardening (S5 §2): an unrecognized name is defense-in-
        // depth only — Anthropic never invokes a tool outside the `tools`
        // array this component itself sent — but log rather than assume
        // every tool_use matches one of the known names.
        onToolUse: (name, input, id) => {
          if (sawAnyToolUse) return;
          sawAnyToolUse = true;
          if (
            name === 'ask_clarifying_question' ||
            name === 'resolve_location' ||
            name === 'apply_property_search_filters'
          ) {
            toolUse = { name, input, id };
          } else {
            console.error(`Unrecognized tool_use name from the model: ${name}`, input);
          }
        },
        onDone: () => {},
        onError: (err) => {
          if (err.agentCause === AgentErrorCause.TRUNCATED) {
            // truncated mid-generation — S8 forbids showing the cut-off
            // partial response, so drop the bubble and let the card explain
            streamDone = true;
            if (renderTimer) {
              clearInterval(renderTimer);
              renderTimer = null;
            }
            fullText = '';
            setMessages((prev) => prev.filter((m) => m.id !== streamMsgId));
          }
          // The failure renders as an error card, not an assistant bubble.
          // `errorText` is still recorded so the history tail below never
          // leaves the conversation ending on an unanswered user turn.
          errorText = `Sorry, I hit an error: ${err.message}`;
          setErrorCauseAndSpeak(err.agentCause || AgentErrorCause.UNKNOWN);
        },
      });
    } catch (err) {
      streamDone = true;
      if (renderTimer) {
        clearInterval(renderTimer);
        renderTimer = null;
      }
      errorText = `Sorry, I hit an error: ${err.message}`;
      setMessages((prev) => prev.filter((m) => m.id !== streamMsgId || Boolean(m.content)));
      // a throw means the stream never opened — the service was unreachable
      setErrorCauseAndSpeak(err.agentCause || AgentErrorCause.UNREACHABLE);
      setHistory(withAssistantReply(turnHistory, errorText));
      return;
    }

    streamDone = true;
    await new Promise((resolve) => {
      const check = setInterval(() => {
        if (renderedLength >= fullText.length) {
          clearInterval(check);
          resolve();
        }
      }, STREAM_RENDER_INTERVAL_MS);
    });

    // A turn that ended in an error is a failed turn. The model can still have
    // emitted a complete tool_use before it was cut off mid-turn (max_tokens
    // lands after the tool_use block, not before it), and the error card is
    // already up — so drop the captured call rather than apply filters or
    // resolve a location behind the failure. Falling through to the tail
    // below, instead of returning here, keeps the existing bubble-cleanup and
    // history handling as the single path for a failed turn. Gated on the
    // local `errorText`, not the `errorCause` state this closure can't see
    // updated.
    if (errorText) {
      toolUse = null;
    }

    if (toolUse?.name === 'ask_clarifying_question') {
      const { question, options } = toolUse.input || {};

      // Gate on the inputs, not the formatted output — a truncated/malformed
      // tool call (e.g. a JSON.parse failure upstream) can still leave
      // `formatted` non-empty (just the lead-in prose) if question/options
      // are missing, which would otherwise ask the user to pick from a list
      // that isn't there.
      const hasValidQuestion = Boolean(question) && Array.isArray(options) && options.length > 0;
      if (!hasValidQuestion) {
        console.error('ask_clarifying_question received empty/malformed input', toolUse.input);
        const fallbackText =
          fullText || "Sorry, I didn't quite catch that — could you rephrase your request?";
        setMessages((prev) =>
          prev.map((m) => (m.id === streamMsgId ? { ...m, content: fallbackText } : m))
        );
        setHistory(withAssistantReply(turnHistory, fallbackText));
        return;
      }

      // The tool_use is deliberately NOT recorded in history — see the
      // module-level comment on TOOLS. The rendered text is a faithful
      // record of what the user saw, and keeps this a plain-text turn.
      const formatted = formatClarifyingQuestion(fullText, question, options);
      setMessages((prev) =>
        prev.map((m) => (m.id === streamMsgId ? { ...m, content: formatted } : m))
      );
      setHistory(withAssistantReply(turnHistory, formatted));
      onAssistantResponseComplete?.(formatted);
      return;
    }

    if (toolUse?.name === 'resolve_location') {
      if (!fullText) {
        setMessages((prev) => prev.filter((m) => m.id !== streamMsgId));
      }

      const assistantMsg = {
        role: 'assistant',
        content: [
          ...(fullText ? [{ type: 'text', text: fullText }] : []),
          { type: 'tool_use', id: toolUse.id, name: 'resolve_location', input: toolUse.input },
        ],
      };

      let matches = [];
      let toolError = null;
      try {
        matches = await resolveLocationSuggestions(
          toolUse.input?.query || '',
          SEARCH_SCOPE_LOCATION_TYPES
        );
      } catch (err) {
        toolError = err;
      }

      // Deliberately stripped to {label, locationType} — the ids are the
      // component's business, not the model's (it re-resolves on apply, see
      // that branch). `locationType` is the endpoint's single-letter type
      // spelled out ('T' -> 'state'): the raw letter told the model nothing,
      // so it couldn't distinguish a state match from a city one and fell
      // back to refusing or substituting a city (ATAI-191). The label alone
      // isn't enough either — "Washington" is both a state and a city.
      const toolResultBlock = {
        type: 'tool_result',
        tool_use_id: toolUse.id,
        content: toolError
          ? `Location lookup failed: ${toolError.message}`
          : JSON.stringify(
              matches.map((m) => ({
                label: m.label,
                locationType: LOCATION_TYPE_NAMES[m.type] || 'area',
              }))
            ),
        ...(toolError ? { is_error: true } : {}),
      };

      const continuedHistory = trimHistory([
        ...turnHistory,
        assistantMsg,
        { role: 'user', content: [toolResultBlock] },
      ]);
      setHistory(continuedHistory);

      // Local depth guard matching the design's MAX_ITERATIONS=3, independent
      // of the backend guardrail — bounds unbounded resolve_location
      // round-trips (e.g. repeated no-match) without relying on a different
      // repo's limit the FE has no visibility into.
      if (resolveLocationDepth + 1 >= MAX_RESOLVE_LOCATION_DEPTH) {
        const limitText = "I couldn't resolve that location. Try rephrasing it.";
        setMessages((prev) => [
          ...prev,
          { id: `${Date.now()}_loc_limit`, role: 'assistant', content: limitText },
        ]);
        // continuedHistory ends on the resolve_location tool_result (a user
        // turn) — bailing here instead of recursing must still hand back a
        // history ending on an assistant turn.
        setHistory(withAssistantReply(continuedHistory, limitText));
        return;
      }

      await runConversationLoop(continuedHistory, resolveLocationDepth + 1, applyDepth);
      return;
    }

    if (toolUse?.name === 'apply_property_search_filters') {
      if (!fullText) {
        setMessages((prev) => prev.filter((m) => m.id !== streamMsgId));
      }

      const { summary, filters, location, clearExisting } = toolUse.input || {};

      // ATAI-278: refuse before anything is applied when the model asks for a
      // filter PropStream doesn't have. Until now nothing checked the filter
      // *field name* anywhere on this path — `validateFilters` above checks
      // values only, and the reducer's `constrainObject` drops an unknown key
      // silently, so the search ran on the location alone while the panel
      // reported the filter as applied (ATAI-246).
      //
      // Checked against the reducer's own FilterFields, via isKnownFilterField
      // — the very list `constrainObject` enforces — so this guard can never
      // disagree with what actually gets applied. Checking the backend's model
      // taxonomy instead would reject 92 real filters the model uses correctly
      // today, `estimatedValueGrowthPeriod` among them (ATAI-246 design §4).
      //
      // Reads the raw `filters` rather than `validatedFilters`: a key dropped
      // for a bad *value* is a different problem, and reading the post-
      // validation object would hide exactly the keys we're looking for.
      //
      // Placed at the very top of this branch, deliberately — before the
      // location re-resolution below and before the missing-location guard.
      // An unsupported filter can never be satisfied, so anything done first
      // is thrown away: the earlier version sat after the re-resolution and
      // burned a real `property/suggestionsnew` call on every refusal that
      // also named a location (PR #3502 review, Mani). It only reads
      // `filters`, so nothing here depends on the location being resolved.
      //
      // Refuses the whole request rather than applying the recognised filters
      // and dropping the rest (reviewer decision, ATAI-246 review round 1):
      // a partial apply restages this very bug in a smaller, harder-to-spot
      // form — a result that doesn't match what was asked for, shown as if it
      // did.
      const unsupportedFilterKeys = Object.keys(filters || {}).filter(
        (key) => !isKnownFilterField(key)
      );
      if (unsupportedFilterKeys.length) {
        const blockedText = unsupportedFilterMessage(unsupportedFilterKeys);
        // Drop the streamed bubble unconditionally on this path, not just when
        // it was empty (PR #3502 review, Fahim). The model routinely narrates
        // before emitting the tool_use — the reported transcript opens with
        // "Got it, searching for properties owned by WILLIAMS FRANCENE in
        // Detroit, MI!" — and leaving that on screen means the customer reads
        // a sentence promising a search, then a refusal, which fails this
        // ticket's first acceptance line. On the apply path below that
        // narration is true and is kept; here it never happened, so it goes.
        // The text still reaches the model via `assistantMsg`, so its own
        // history is unchanged. (Filtering an already-removed id is a no-op.)
        setMessages((prev) => prev.filter((m) => m.id !== streamMsgId));
        // A plain assistant message, not a filter_card (reviewer decision) —
        // same treatment as the missing-location refusal below, and nothing
        // was applied, so the "SEARCH FILTERS" chrome would be a lie.
        // Own id rather than the apply path's `cardId`: this guard now runs
        // before that is declared, and nothing is applied here anyway.
        setMessages((prev) => [
          ...prev,
          { id: `${Date.now()}_unsupported`, role: 'assistant', content: blockedText },
        ]);

        const assistantMsg = {
          role: 'assistant',
          content: [
            ...(fullText ? [{ type: 'text', text: fullText }] : []),
            {
              type: 'tool_use',
              id: toolUse.id,
              name: 'apply_property_search_filters',
              input: toolUse.input,
            },
          ],
        };
        const toolResultBlock = {
          type: 'tool_result',
          tool_use_id: toolUse.id,
          // The offending field names go to the model but never to the user:
          // it needs to know which field it invented so its follow-up is
          // specific and it doesn't just retry the same call.
          content:
            `${blockedText}\n\nNothing was applied — no filters, no location, no search. ` +
            `Unsupported filter field(s): ${unsupportedFilterKeys.join(', ')}. ` +
            'Do not call this tool again for the same field; tell the user what you cannot do ' +
            'and offer only filters from the taxonomy.',
          is_error: true,
        };
        const continuedHistory = trimHistory([
          ...turnHistory,
          assistantMsg,
          { role: 'user', content: [toolResultBlock] },
        ]);
        setHistory(continuedHistory);

        // Same accounting as the location guard: an apply retry counts against
        // applyDepth, not resolveLocationDepth.
        if (applyDepth + 1 >= MAX_APPLY_DEPTH) {
          const limitText =
            "I still can't run that search with the filters available. Try describing the " +
            'properties a different way.';
          setMessages((prev) => [
            ...prev,
            { id: `${Date.now()}_apply_limit`, role: 'assistant', content: limitText },
          ]);
          setHistory(withAssistantReply(continuedHistory, limitText));
          return;
        }

        await runConversationLoop(continuedHistory, resolveLocationDepth, applyDepth + 1);
        return;
      }

      // resolve_location's tool_result is deliberately stripped to
      // {label, locationType} (see that branch above) — re-resolve here
      // rather than thread the full match object through the model, per the
      // S4 PR review's own call: keep tool_results minimal, re-derive on
      // demand.
      // The model's guessed geo keys are dropped before anything reads them
      // (ATAI-225 — `stateId: 48` is Texas's FIPS and Pennsylvania's
      // `view_states.id`, so the search ran on Pennsylvania under a card
      // reading "Texas, TX"). What survives is what the model can legitimately
      // hold; a state/zip/county location therefore no longer short-circuits
      // here and falls through to the re-resolve below, which takes the id off
      // the real suggestion row.
      const trustedModelLocation = stripUnverifiableLocationKeys(location);
      let resolvedLocation = hasSupportedLocation(trustedModelLocation)
        ? trustedModelLocation
        : null;
      if (!resolvedLocation && location?.label) {
        try {
          const matches = await resolveLocationSuggestions(
            location.label,
            SEARCH_SCOPE_LOCATION_TYPES
          );
          resolvedLocation = pickLocationMatch(matches, location);
        } catch (err) {
          resolvedLocation = null;
        }
      }
      // Remap before the supported-location check — a state row arrives as
      // raw {id, type: 'T', label} with no stateId of its own (the endpoint
      // selects no state_id column), so checking hasSupportedLocation on it
      // directly would reject a real statewide match (e.g. Texas) as if it
      // carried no location at all.
      resolvedLocation = remapSuggestionLocationFields(resolvedLocation);

      // Fallback order, widest-trust first (ATAI-200): the location the model
      // just resolved; then this turn's own post-apply location
      // (`extraContext`, which the request payload above already prefers over
      // the prop — `context` is the parent's redux-derived prop and hasn't
      // re-rendered yet inside a recursion chain); then the prop; and finally
      // the conversation's own last applied location, which survives a redux
      // `location` that some other Search flow has since overwritten. Omitting
      // `location` on a follow-up filter is the model doing what the tool
      // description tells it to — "omit it to keep whatever location is already
      // active" — so it must not be what ends the search.
      //
      // Each candidate is tested with hasSupportedLocation rather than for
      // truthiness: `resolvedLocation` can be a truthy row the search cannot
      // use — pickLocationMatch's last resort is the endpoint's top-ranked row
      // whatever its type, and a street row carries no id at all while a
      // property row remaps to `propertyId`, which is not one of
      // either supported-location key list. A truthy-but-unusable candidate
      // short-circuiting the chain is what made this fix a no-op on the
      // follow-up-filter turn it exists for (ATAI-200 retest, 2026-09-11).
      const memoryLocation =
        [
          extraContext?.currentLocation,
          context?.currentLocation,
          readLastAppliedLocation(sessionIdRef.current),
        ].find((candidate) => hasSupportedLocation(candidate)) || null;
      // A location the model actually named this turn, whether or not it
      // resolved — an empty object is not a naming. A guessed key is not a
      // naming either (ATAI-225): counting one would send a `location` that is
      // nothing but a fabricated `stateId` down the "named but unresolvable"
      // branch below, and the agent would ask for a location the user already
      // gave, which is ATAI-200 all over again. Blanking those keys drops the
      // turn to the memory branch instead, so the conversation keeps searching
      // where it already was — with the guess discarded either way.
      const modelNamedLocation =
        Boolean(location?.label) || hasSupportedLocation(trustedModelLocation);
      let effectiveLocation;
      if (hasSupportedLocation(resolvedLocation)) {
        // named and resolved to a searchable row — search it
        effectiveLocation = resolvedLocation;
      } else if (modelNamedLocation) {
        // named but unresolvable — ask, rather than silently searching the
        // conversation's previous location under the name the user just gave
        effectiveLocation = null;
      } else {
        // no location this turn — keep whatever the conversation is searching
        effectiveLocation = memoryLocation;
      }
      const validatedFilters = validateFilters(filters, true);
      const cardId = `${Date.now()}_card`;

      if (!hasSupportedLocation(effectiveLocation)) {
        const blockedText =
          'I need a location to search. Try naming a state, city, county, or zip code.';
        // A plain assistant message, not a filter_card — nothing was applied,
        // so wrapping this in the "SEARCH FILTERS" card chrome would show an
        // error explanation as if it were a search summary.
        setMessages((prev) => [...prev, { id: cardId, role: 'assistant', content: blockedText }]);

        const assistantMsg = {
          role: 'assistant',
          content: [
            ...(fullText ? [{ type: 'text', text: fullText }] : []),
            {
              type: 'tool_use',
              id: toolUse.id,
              name: 'apply_property_search_filters',
              input: toolUse.input,
            },
          ],
        };
        const toolResultBlock = {
          type: 'tool_result',
          tool_use_id: toolUse.id,
          content: blockedText,
          is_error: true,
        };
        const continuedHistory = trimHistory([
          ...turnHistory,
          assistantMsg,
          { role: 'user', content: [toolResultBlock] },
        ]);
        setHistory(continuedHistory);

        // This is an apply retry, not a resolve_location retry — counts
        // against applyDepth, not resolveLocationDepth (see MAX_APPLY_DEPTH's
        // comment for why sharing one counter is wrong).
        if (applyDepth + 1 >= MAX_APPLY_DEPTH) {
          const limitText = "I still don't have a location for this search.";
          setMessages((prev) => [
            ...prev,
            { id: `${Date.now()}_apply_limit`, role: 'assistant', content: limitText },
          ]);
          setHistory(withAssistantReply(continuedHistory, limitText));
          return;
        }

        await runConversationLoop(continuedHistory, resolveLocationDepth, applyDepth + 1);
        return;
      }

      // Applying is automatic the moment the tool_use arrives (Figma "AI
      // Search - LONG CHAT" shows only the already-applied state, no
      // pending confirmation step). No Undo — cut for v1.
      const appliedFilters = applyResolvedSearch(dispatch, store, {
        validatedFilters,
        effectiveLocation,
        clearExisting,
      });
      // Remember what this conversation actually searched, past the guard and
      // past the dispatch (ATAI-200) — see readLastAppliedLocation's comment.
      // Only a location the user confirmed and the agent applied is ever stored
      // here; an ambient redux location the conversation never picked is not
      // something to silently search on their behalf.
      writeLastAppliedLocation(sessionIdRef.current, effectiveLocation);
      // ATAI-286: record which search the grid now shows. Dispatched right after
      // the synchronous apply, before the wait (up to 5 minutes), so a manual
      // filter edit made while results load is never recorded as the AI's.
      dispatch(setAiSearchLabel(summary));
      appliedThisTurnRef.current = true;

      const { status: resultStatus, count: resultCount } = await waitForSearchResults();
      // The filters are applied either way — only the count is unknown. Say so
      // in the panel rather than letting the turn end on a number nobody can
      // vouch for (ATAI-192).
      if (resultStatus !== SearchResultStatus.CONFIRMED) {
        setErrorCause(SEARCH_TIMED_OUT);
      }
      // Read back the actual post-dispatch state rather than reusing
      // validatedFilters/effectiveLocation — when clearExisting is false the
      // real current filter is old+new merged, not just this turn's deltas.
      const postApplyState = store.getState();
      // ATAI-279: falls back to the applied set, not the requested one — the
      // fallback should never be the route a dropped key sneaks back in by.
      const postApplyFilters = selectFilter(postApplyState)?.toJS() || appliedFilters;
      const postApplyLocation = selectLocation(postApplyState)?.toJS() || effectiveLocation;
      // Same reason as postApplyFilters, for the chip list the agent reports
      // from (ATAI-204): after a clearExisting: false merge the panel shows
      // old+new chips, which is what the user will be counting.
      const postApplyAppliedFilters = toAppliedFilterContext(selectLocalFilter(postApplyState));

      setMessages((prev) => [
        ...prev,
        {
          id: cardId,
          role: 'filter_card',
          summary: summary || 'this search',
          // ATAI-279: the chips must name what the search actually ran with.
          // This card says "Applied to search", so listing a filter the
          // reducer dropped is the panel stating something untrue (ATAI-246).
          filters: appliedFilters,
          location: effectiveLocation,
          resultCount,
        },
      ]);
      // ATAI-218: the panel used to collapse here once a search applied
      // (PROPS-3641), but Product confirmed it should stay open — same as
      // the ambiguous-prompt case — so the AI Search conversation can
      // continue without the user needing to manually reopen it. Only an
      // explicit close (or switching away from AI Search) should collapse
      // it now.

      const assistantMsg = {
        role: 'assistant',
        content: [
          ...(fullText ? [{ type: 'text', text: fullText }] : []),
          {
            type: 'tool_use',
            id: toolUse.id,
            name: 'apply_property_search_filters',
            input: toolUse.input,
          },
        ],
      };
      // result_count travels through this tool_result per the design (S2
      // §2) — unlike ask_clarifying_question, applying is synchronous and
      // automatic, so there's no user-interaction gap that could leave this
      // tool_use dangling; safe to close it with the real outcome.
      //
      // result_count_status rides along because a bare `result_count: null`
      // was being read as "zero properties matched" — the model then told the
      // user nothing was found while the grid filled with results (ATAI-192).
      const toolResultBlock = {
        type: 'tool_result',
        tool_use_id: toolUse.id,
        content: JSON.stringify({
          applied: true,
          result_count: resultCount,
          result_count_status: resultStatus,
        }),
      };
      const continuedHistory = trimHistory([
        ...turnHistory,
        assistantMsg,
        { role: 'user', content: [toolResultBlock] },
      ]);
      setHistory(continuedHistory);

      // context.searchSnapshot (Story 5 §2) — sent on this same request;
      // this story's job ends at sending it, Story 7 (ATAI-153, already
      // merged) owns persistence. Field names match ATAI-153's own contract
      // exactly (its ticket description spells out this shape) — S5 is the
      // producer, S7 the consumer; the wrong key names here would silently
      // persist null location/schema version on every applied search.
      //
      // Confirmed live (2026-09-08): this is NOT opaque passthrough JSON —
      // the backend has its own fixed shape for this column. Sending an
      // extra `resolvedLocation` object here round-tripped back as
      // `resolvedLocationId`/`resolvedLocationLabel: null` — the backend
      // silently dropped the field it didn't recognize. So this keeps the
      // original two fields; buildResumeState re-resolves the location from
      // resolvedLocationLabel instead (see its own comment for how that
      // re-resolution is now tightened against a wrong-location match).
      const searchSnapshot = {
        resolvedLocationId:
          effectiveLocation?.cityId ??
          effectiveLocation?.countyId ??
          effectiveLocation?.stateId ??
          effectiveLocation?.zip ??
          effectiveLocation?.fips ??
          null,
        resolvedLocationLabel: effectiveLocation?.label ?? null,
        // ATAI-279: persist only what was applied. A phantom key stored here
        // outlives the session — it comes back on the next visit's "Welcome
        // back" card and, if Continue is pressed, is offered as a filter the
        // search never had.
        filters: appliedFilters,
        summary,
        toolSchemaVersion: APPLY_FILTERS_SCHEMA_VERSION,
        resultCount,
      };
      // currentFilters/currentLocation must be overridden too, not just
      // searchSnapshot added — `context` is the parent's Redux-derived prop
      // and hasn't re-rendered yet with the dispatches above, so without
      // this the model would receive a fresh searchSnapshot next to stale
      // currentFilters/currentLocation on the very same request.
      // Bounds the model calling apply_property_search_filters again off its
      // own tool_result — previously unconditional recursion (Fahim's PR
      // #3344 review round 4, MAJOR): a real dispatch + /listing fetch + up
      // to a 15s wait per iteration, with no bound. Uses applyDepth, not
      // resolveLocationDepth (see MAX_APPLY_DEPTH's comment).
      if (applyDepth + 1 >= MAX_APPLY_DEPTH) {
        const limitText =
          "I've applied several searches in a row — let me know what to adjust next.";
        setMessages((prev) => [
          ...prev,
          { id: `${Date.now()}_apply_limit`, role: 'assistant', content: limitText },
        ]);
        setHistory(withAssistantReply(continuedHistory, limitText));
        return;
      }

      await runConversationLoop(continuedHistory, resolveLocationDepth, applyDepth + 1, {
        searchSnapshot,
        currentFilters: postApplyFilters,
        appliedFilters: postApplyAppliedFilters,
        currentLocation: postApplyLocation,
      });
      return;
    }

    if (!fullText) {
      setMessages((prev) => prev.filter((m) => m.id !== streamMsgId));
    }
    // Whatever text was actually shown (streamed text, or the error bubble
    // onError/catch already rendered) becomes the recorded assistant turn,
    // so history never ends on an unanswered user turn — even a genuinely
    // empty response gets a minimal placeholder rather than breaking the
    // next turn's role alternation.
    setHistory(
      withAssistantReply(
        turnHistory,
        fullText || errorText || "Sorry, I didn't get a response. Please try again."
      )
    );
    if (fullText) onAssistantResponseComplete?.(fullText);
  }

  function handleSend(rawText) {
    const text = (rawText ?? inputText).trim();
    if (!text || loading) return false;

    if (!rawText) setInputText('');

    // F4: suppress a still-in-flight "Welcome back" resume card once the
    // user has sent anything of their own — see userSubmittedRef above.
    userSubmittedRef.current = true;

    // The design shows an over-long prompt echoed into the panel with the
    // "too long" card beneath it, so the bubble is pushed before the guard
    // runs rather than after it.
    const userMsg = { id: `${Date.now()}_u`, role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);

    if (countWords(text) > MAX_PROMPT_WORDS) {
      // over the client-side word guard — shown, but never sent, and
      // deliberately left out of `history`: the model never saw this turn, so
      // recording it would leave the conversation ending on a user turn.
      // Still `true`: the prompt was handled (echoed + card shown), so the
      // header must consume it rather than hold it as pending.
      setErrorCauseAndSpeak(TOO_LONG);
      return true;
    }
    // within the guard — clear any prior failure and send
    setErrorCause(null);
    setPersistentSearchChatContent(true);
    lastPromptRef.current = text;

    // Always an ordinary plain-text turn — ask_clarifying_question's
    // tool_use is never recorded in history (see the TOOLS comment), so
    // there is never a pending tool_use to close here, clarification reply
    // or not.
    const newHistory = trimHistory([...history, { role: 'user', content: text }]);

    setLoading(true);
    onLoadingChange?.(true);
    appliedThisTurnRef.current = false;
    runConversationLoop(newHistory).finally(() => {
      setLoading(false);
      onLoadingChange?.(false);
      // ATAI-286: the reply ran no search (a refusal, a question, a general
      // answer or an error), so the results on screen are from an earlier one.
      // Keyed on "nothing was applied", not on detecting a refusal: most
      // refusals come from the model itself and are plain text to this panel.
      if (!appliedThisTurnRef.current) dispatch(markAiSearchLabelEarlier());
    });
    return true;
  }

  // Waits on the *existing* `stale`-flag search-refresh watcher (Search/
  // index.js's componentDidUpdate — unchanged, per the design) rather than
  // issuing a second, duplicate fetch of our own: wait for it to pick up the
  // dispatch below, run the search, and land a result, then read the settled
  // resultCount off the active context.
  //
  // Returns { status, count } rather than a bare number, and `count` is a
  // number ONLY for status 'confirmed' (ATAI-192). The old version returned
  // `resultCountRef.current ?? 0` once `loading` went false or its 15s budget
  // ran out — but `loading` also drops to false on a failed search, and
  // applyResolvedSearch's clearFilter() resets the context's resultCount to 0
  // before the new search even starts, so a slow or failed /listing call fed
  // the model a confident `0` while the grid went on to render tens of
  // thousands of rows. Every not-actually-confirmed path now reports null plus
  // a status, and the caller renders a timed-out message instead of a count.
  async function waitForSearchResults(timeoutMs = SEARCH_RESULT_TIMEOUT_MS) {
    const baselineVersion = listingResultVersionRef.current;
    const start = Date.now();
    let notifiedSlow = false;

    // Bumped only by SEARCH_LISTING_SUCCESS, so this is true exactly when the
    // search we triggered came back — a genuine 0 included.
    const hasSettled = () => listingResultVersionRef.current !== baselineVersion;
    // The watcher is gated on `!loading` (Search/index.js), so a search queued
    // behind one still in flight shows up as stale-but-not-yet-loading. Both
    // states mean "still coming", and waiting through them is what stopped the
    // old 2s start window from giving up on a deferred search.
    const searchPending = () => Boolean(searchStaleRef.current || searchLoadingRef.current);
    const failure = () => {
      const err = listingErrorRef.current;
      return err && (err.get('at') || 0) >= start ? err : null;
    };

    while (!hasSettled() && !failure() && Date.now() - start < timeoutMs) {
      // Nothing consumed the dispatch inside the grace window — the search
      // never started, so there is no result coming to wait for.
      if (!searchPending() && Date.now() - start > SEARCH_START_GRACE_MS) {
        break;
      }
      // Long enough that the user deserves to know the panel hasn't stalled.
      if (!notifiedSlow && Date.now() - start >= SLOW_SEARCH_NOTICE_MS) {
        notifiedSlow = true;
        setMessages((prev) => [
          ...prev,
          {
            id: `${Date.now()}_still_loading`,
            role: 'status_note',
            content: 'Still loading results…',
          },
        ]);
      }
      await new Promise((r) => setTimeout(r, 50));
    }

    // Settled: the count in the context is this search's, 0 included.
    if (hasSettled()) {
      return { status: SearchResultStatus.CONFIRMED, count: resultCountRef.current ?? 0 };
    }
    // The request came back as an error — a gateway/request timeout reads
    // differently to the user than any other failure.
    const err = failure();
    if (err) {
      return {
        status: err.get('timeout') ? SearchResultStatus.TIMEOUT : SearchResultStatus.ERROR,
        count: null,
      };
    }
    // Our own budget ran out with the search still in flight.
    if (Date.now() - start >= timeoutMs) {
      return { status: SearchResultStatus.TIMEOUT, count: null };
    }
    // The watcher never picked the dispatch up at all.
    return { status: SearchResultStatus.NOT_STARTED, count: null };
  }

  // Only used by the commented-out chatInputRow below (prompts now come
  // through the header's own AI Search bar instead).
  // function handleInputChange(e) {
  //   setInputText(e.target.value);
  //   if (errorCause) setErrorCause(null);
  // }

  // function handleKeyDown(e) {
  //   if (e.key === 'Enter' && !e.shiftKey) {
  //     e.preventDefault();
  //     handleSend();
  //   }
  // }

  // Also only used by the commented-out chatInputRow below.
  // const overLimit = countWords(inputText) > MAX_PROMPT_WORDS;
  const showUnavailableModal = open && errorCause === AgentErrorCause.UNREACHABLE;
  const errorCard = errorCause && !showUnavailableModal ? errorCardFor(errorCause) : null;

  function handleRetry() {
    setErrorCause(null);
    if (lastPromptRef.current) {
      // resend the prompt that failed
      handleSend(lastPromptRef.current);
    }
  }

  function handleUseLocationSearch() {
    setErrorCause(null);
    onUseLocationSearch?.();
  }

  // Anchored the same way SuggestedInput's own suggestion dropdown is
  // (react-portal + a viewport-relative rect from the search box) — a fixed
  // 420px-tall panel positioned inside the header's own fixed-height flex row
  // would just overflow that row and overlay the map/results below it,
  // exactly like a dropdown does, but persistent for the whole conversation.
  // Rendering into a body-level Portal keeps it floating over the page
  // without needing the header row (or anything below it) to resize.
  // positionStyle itself is measured in the layout effect above.

  return (
    <Portal isOpened>
      <div
        className={classNames(css.chatPanel, {
          [css.chatPanelHidden]: !open,
        })}
        style={positionStyle}
      >
        <div className={css.chatMessages}>
          {messages.map((msg) => (
            <MessageItem
              key={msg.id}
              msg={msg}
              onContinueSearch={handleContinueSearch}
              onStartFresh={handleStartFresh}
            />
          ))}
          {loading && !messages.find((m) => m.id === activeStreamId)?.content && <LoadingDots />}
          {!loading && historyLoading && messages.length === 0 && <LoadingDots />}
          <VoiceTranscriptActivity transcript={voiceTranscript} />
          {errorCard && <ErrorMessage card={errorCard} onRetry={handleRetry} />}
          <div ref={messagesEndRef} />
        </div>
        {/* Users continue typing additional prompts directly into the AI Search
            bar (the header's own search box), not a second input here — this
            panel is read-only display + the Close icon (no filter side-effects). */}
        {/* <div className={css.chatInputRow}>
          <input
            type="text"
            className={css.chatInput}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask a follow-up…"
            disabled={loading}
          />
          <button
            type="button"
            className={css.chatSendBtn}
            onClick={() => handleSend()}
            disabled={loading || !inputText.trim() || overLimit}
          >
            Send
          </button>
        </div> */}
      </div>
      {showUnavailableModal && (
        <UnavailableModal onRetry={handleRetry} onUseLocationSearch={handleUseLocationSearch} />
      )}
    </Portal>
  );
}

SearchChatPanel.propTypes = {
  open: PropTypes.bool,
  initialPrompt: PropTypes.string,
  onConsumeInitialPrompt: PropTypes.func,
  context: PropTypes.object,
  anchorRef: PropTypes.object,
  dividerRef: PropTypes.object,
  onLoadingChange: PropTypes.func,
  onSubmitError: PropTypes.func,
  onAssistantResponseComplete: PropTypes.func,
  onChatContentChange: PropTypes.func,
  voiceTranscript: PropTypes.string,
  voiceActive: PropTypes.bool,
  onUseLocationSearch: PropTypes.func,
};

SearchChatPanel.defaultProps = {
  open: false,
  initialPrompt: null,
  onConsumeInitialPrompt: () => {},
  context: {},
  onLoadingChange: () => {},
  onSubmitError: () => {},
  onAssistantResponseComplete: () => {},
  onChatContentChange: () => {},
  voiceTranscript: '',
  voiceActive: false,
  onUseLocationSearch: () => {},
};
